import * as THREE from 'three'
import { buildScene, ANIM_WORLD_Y, ENV_WORLD_Y, getEnvMap, generateEnvMap, envShadowLight, globalLightsToMute } from './scene.js'
import { buildPostFX }      from './postfx.js'
import { buildParticles }   from './particles.js'
import { buildSkyDome }     from './sky.js'
import { buildScrollDriver } from './scroll.js'
import GUI from 'lil-gui'

// ── Renderer ──────────────────────────────────────────────────────────────────
const container = document.getElementById('canvas-container')
const isMobile  = window.innerWidth <= 1024  // covers all phones + small tablets

const renderer = new THREE.WebGLRenderer({
  antialias:       !isMobile,
  powerPreference: 'high-performance',
})
// Desktop: cap DPR at 1.5. Mobile: DPR 1.0 — halves the fragment/bloom cost on
// high-DPI phones (a 3x screen would otherwise fill a huge half-float buffer each frame)
renderer.setPixelRatio(isMobile ? 1.0 : Math.min(window.devicePixelRatio, 1.5))
renderer.setSize(container.clientWidth, container.clientHeight)
// Shadows off on mobile: 2 shadow-casting lights re-render their maps every frame,
// and a newly-visible model entering the shadow pass caused the hitch when scrolling
// between sections. Desktop keeps them.
renderer.shadowMap.enabled = !isMobile
renderer.shadowMap.type = THREE.PCFSoftShadowMap
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 0.9
container.appendChild(renderer.domElement)

// ── Scene & Camera ────────────────────────────────────────────────────────────
const scene = new THREE.Scene()

const camera = new THREE.PerspectiveCamera(
  45,
  container.clientWidth / container.clientHeight,
  0.1,
  100
)
camera.position.set(0, 0, 5)
camera.lookAt(0, 0, 0)

// ── Post-FX ───────────────────────────────────────────────────────────────────
const { composer, bloomPass, lensBlurPass, grainPass } = buildPostFX(renderer, scene, camera, isMobile)

// ── Resize (debounced — rapid resize on mobile URL-bar show/hide can crash WebGL) ──
let resizeTimer
function onResize() {
  clearTimeout(resizeTimer)
  resizeTimer = setTimeout(() => {
    const w = container.clientWidth
    const h = container.clientHeight
    camera.aspect = w / h
    camera.updateProjectionMatrix()
    renderer.setSize(w, h)
    // composer uses a custom RT so its internal _pixelRatio=1 — must pass
    // DPR-scaled pixel count directly, otherwise it drops to CSS resolution
    const draw = renderer.getDrawingBufferSize(new THREE.Vector2())
    composer.setSize(draw.width, draw.height)
  }, 100)
}
window.addEventListener('resize', onResize)
window.addEventListener('orientationchange', () => setTimeout(onResize, 200))

// ── WebGL context loss — recover automatically ────────────────────────────────
renderer.domElement.addEventListener('webglcontextlost', (e) => {
  e.preventDefault()   // allow context to be restored
}, false)
renderer.domElement.addEventListener('webglcontextrestored', () => {
  // Three.js restores itself; just re-trigger a resize to resync render targets
  onResize()
}, false)

// ── Mouse parallax (desktop only) ────────────────────────────────────────────
const mouse     = { x: 0, y: 0 }  // raw input (-1…+1)
const mouseLerp = { x: 0, y: 0 }  // smoothed output (lerped each frame)

if (!isMobile) {
  window.addEventListener('mousemove', e => {
    mouse.x =  (e.clientX / window.innerWidth  - 0.5) * 2
    mouse.y = -(e.clientY / window.innerHeight - 0.5) * 2
  })
}

// ── Init ──────────────────────────────────────────────────────────────────────
const clock = new THREE.Clock()

// ── Easing ────────────────────────────────────────────────────────────────────
function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
}

function lerp(a, b, t) { return a + (b - a) * t }

// ── Blender-style cone bone visualiser ───────────────────────────────────────
// Builds one 4-sided cone mesh per bone→child pair + a sphere at every joint,
// positioned in world-space each frame so they track the animated skeleton.
function buildBoneVisualizer(skeleton, scene) {
  // Cone: base at y=0 (head), tip at y=1 (tail). Scaled each frame by bone length.
  const coneBase = new THREE.ConeGeometry(1, 1, 4, 1)
  coneBase.applyMatrix4(new THREE.Matrix4().makeTranslation(0, 0.5, 0))
  const sphereBase = new THREE.SphereGeometry(1, 6, 4)

  const mat = () => new THREE.MeshBasicMaterial({
    color: 0xaa99cc,           // purple-tinted gray — matches site palette
    transparent: true,
    opacity: 0,
    depthTest: false,
    depthWrite: false,
    side: THREE.DoubleSide,
  })

  const cones  = []  // { bone, child, mesh }
  const joints = []  // { bone, mesh }

  skeleton.bones.forEach(bone => {
    // Sphere at every joint
    const sph = new THREE.Mesh(sphereBase, mat())
    scene.add(sph)
    joints.push({ bone, mesh: sph })

    // Cone from this bone to its first child bone
    const child = bone.children.find(c => c.isBone)
    if (!child) return
    const cone = new THREE.Mesh(coneBase, mat())
    scene.add(cone)
    cones.push({ bone, child, mesh: cone })
  })

  const _h   = new THREE.Vector3()
  const _t   = new THREE.Vector3()
  const _dir = new THREE.Vector3()
  const _up  = new THREE.Vector3(0, 1, 0)
  const _q   = new THREE.Quaternion()
  const _ax  = new THREE.Vector3(1, 0, 0)

  function update(opacity) {
    const show = opacity > 0.005

    cones.forEach(({ bone, child, mesh }) => {
      bone.getWorldPosition(_h)
      child.getWorldPosition(_t)
      const len = _h.distanceTo(_t)
      if (len < 0.001) { mesh.visible = false; return }

      _dir.subVectors(_t, _h).normalize()
      // Guard against antiparallel singularity
      Math.abs(_dir.dot(_up) + 1) < 0.001
        ? _q.setFromAxisAngle(_ax, Math.PI)
        : _q.setFromUnitVectors(_up, _dir)

      mesh.position.copy(_h)
      mesh.quaternion.copy(_q)
      mesh.scale.set(len * 0.11, len, len * 0.11)
      mesh.material.opacity = opacity
      mesh.visible = show
    })

    joints.forEach(({ bone, mesh }) => {
      bone.getWorldPosition(_h)
      mesh.position.copy(_h)
      mesh.scale.setScalar(0.016)
      mesh.material.opacity = Math.min(opacity * 1.3, 1)
      mesh.visible = show
    })
  }

  return { update }
}

async function init() {
  const { model1, model2, model3, model4, model5, model6, animModel, animClips, envModel, lgPurple, lgYellow, lgGreen, heroYellow, heroRed, sectionGap, carouselGap } = await buildScene(scene, isMobile)

  const { mesh: skyMesh, update: updateSky } = buildSkyDome(scene)
  const { update: updateParticles } = buildParticles(scene)
  const heroTagline          = document.getElementById('hero-tagline')
  const heroFeatures         = document.getElementById('hero-features')
  const heroDecor            = document.getElementById('hero-decor')
  const aboutSection         = document.getElementById('about-section')
  const lgColorPicker        = document.getElementById('lg-color-picker')
  const heroColorPicker      = document.getElementById('hero-color-picker')
  const charactersSection    = document.getElementById('characters-section')
  const environmentsSection  = document.getElementById('environments-section')
  const animationSection     = document.getElementById('animation-section')
  const contactSection       = document.getElementById('contact-section')
  const charFeatures         = document.getElementById('char-features')
  const collectiblesSection  = document.getElementById('collectibles-section')
  // Per-deck video split: front cards always play while the section is visible;
  // the cards hidden behind them only decode while their deck is fanned open.
  const collectStacks = Array.from(document.querySelectorAll('.collect-stack')).map(stack => ({
    el:         stack,
    frontVideo: stack.querySelector('.collect-card:first-child video'),
    backVideos: Array.from(stack.querySelectorAll('.collect-card:not(:first-child) video')),
  }))
  const setVideoPlaying = (v, on) => {
    if (!v) return
    if (on && v.paused) v.play().catch(() => {})
    else if (!on && !v.paused) v.pause()
  }

  // ── Animation mixer — plays all clips from the GLB in an infinite loop ───
  const animMixer = animClips && animClips.length > 0
    ? new THREE.AnimationMixer(animModel)
    : null
  if (animMixer) {
    animClips.forEach(clip => {
      const action = animMixer.clipAction(clip)
      action.setLoop(THREE.LoopRepeat, Infinity)
      action.play()
    })
  }
  const animClock = new THREE.Clock()  // separate clock — avoids conflicting with getElapsedTime()

  // ── Cone bone visualiser — Blender-style ────────────────────────────────
  let boneSkeleton = null
  animModel.traverse(obj => { if (obj.isSkinnedMesh && !boneSkeleton) boneSkeleton = obj.skeleton })
  const boneViz = boneSkeleton ? buildBoneVisualizer(boneSkeleton, scene) : null

  // ── Timeline UI — build keyframe dots + tick labels once ────────────────
  const tlTrack       = document.getElementById('anim-track')
  const tlPlayhead    = document.getElementById('anim-playhead')
  const tlFrameDisp   = document.getElementById('anim-frame-display')
  const tlClipName    = document.getElementById('anim-clip-name')
  const tlNumbers     = document.getElementById('anim-tl-numbers')
  const tlEl          = document.getElementById('anim-timeline')
  const mainClip      = animClips && animClips.length > 0 ? animClips[0] : null
  const FPS           = 24

  if (mainClip) {
    tlClipName.textContent = mainClip.name || 'Animation'
    const totalFrames = Math.round(mainClip.duration * FPS)

    // Collect unique keyframe positions (% along track) — deduplicated + sorted
    const kfSet = new Set()
    mainClip.tracks.forEach(track => {
      track.times.forEach(t => kfSet.add(Math.round(t * FPS)))
    })
    const kfFrames = Array.from(kfSet).sort((a, b) => a - b)

    // Inject dots
    kfFrames.forEach(f => {
      const dot = document.createElement('div')
      dot.className = 'anim-kf-dot'
      dot.style.left = `${(f / totalFrames) * 100}%`
      tlTrack.appendChild(dot)
    })

    // Inject tick labels (every ~48 frames)
    const tickStep = 48
    for (let f = 0; f <= totalFrames; f += tickStep) {
      const sp = document.createElement('span')
      sp.textContent = f
      tlNumbers.appendChild(sp)
    }
  }
  const scrollHint     = document.getElementById('scroll-hint')
  const rotateHint     = document.getElementById('rotate-hint')
  const dots           = Array.from(document.querySelectorAll('.s-dot'))

  const { update: updateScroll, goTo: scrollTo, jumpTo: scrollJump, setSnaps, getIndex: getSnapIndex, setHorizontalHandler, isTweening } = buildScrollDriver(isMobile)

  // ── Nav instant jump with canvas fade ────────────────────────────────────
  const canvasContainer = document.getElementById('canvas-container')
  canvasContainer.style.transition = 'opacity 0.3s ease'

  function navJump(targetValue, extraFn) {
    canvasContainer.style.opacity = '0'
    setTimeout(() => {
      scrollJump(targetValue)
      if (extraFn) extraFn()
      canvasContainer.style.opacity = '1'
    }, 300)
  }

  // Navigation
  function goHome()       { navJump(0, () => { autoAngle = 0.38; autoAngle2 = 0.0 }) }
  function goAbout()      { navJump(750) }
  function goCharacters() { navJump(P2_END) }
  function goAnimation()  { navJump(P_ANIM_END) }
  function goContact()    { navJump(P_CONTACT_END) }

  // Corner logo + HOME nav link → sweep back to top
  document.getElementById('corner-logo').addEventListener('click', goHome)
  const navLinks    = document.querySelectorAll('#nav-links a')
  const drawerLinks = document.querySelectorAll('#mobile-drawer .drawer-link')
  navLinks[0]?.addEventListener('click', goHome)
  navLinks[1]?.addEventListener('click', goAbout)
  navLinks[2]?.addEventListener('click', goCharacters)
  navLinks[3]?.addEventListener('click', goAnimation)
  navLinks[4]?.addEventListener('click', goContact)
  drawerLinks[0]?.addEventListener('click', goHome)
  drawerLinks[1]?.addEventListener('click', goAbout)
  drawerLinks[2]?.addEventListener('click', goCharacters)
  drawerLinks[3]?.addEventListener('click', goAnimation)
  drawerLinks[4]?.addEventListener('click', goContact)

  // Hero CTA buttons — View Our Work leads into the Studio section
  document.getElementById('btn-view-work')?.addEventListener('click', goAbout)
  document.getElementById('btn-lets-talk')?.addEventListener('click', goContact)

  // Collectible stacks — tap toggles the fan on touch devices (hover covers desktop)
  document.querySelectorAll('.collect-stack').forEach(stack => {
    stack.addEventListener('click', () => stack.classList.toggle('fanned'))
  })

  // ── Galleries (characters + environments) ─────────────────────────────────
  const galleryHint    = document.getElementById('gallery-hint')
  const envGalleryHint = document.getElementById('env-gallery-hint')
  const charGallery    = document.getElementById('char-gallery')
  const envGallery     = document.getElementById('env-gallery')
  const galleries      = [charGallery, envGallery].filter(Boolean)
  const charLightbox   = document.getElementById('char-lightbox')
  const lightboxImg    = charLightbox?.querySelector('img')

  function closeLightbox() { charLightbox?.classList.remove('open') }
  function openGallery(overlay) {
    overlay?.classList.add('open')
    document.body.classList.add('gallery-open')   // pauses the snap-scroll driver
  }
  function closeGalleries() {
    galleries.forEach(g => g.classList.remove('open'))
    document.body.classList.remove('gallery-open')
    closeLightbox()
  }

  galleryHint?.addEventListener('click', () => openGallery(charGallery))
  envGalleryHint?.addEventListener('click', () => openGallery(envGallery))
  document.getElementById('gallery-close')?.addEventListener('click', closeGalleries)
  document.getElementById('env-gallery-close')?.addEventListener('click', closeGalleries)
  // Let the galleries scroll natively — stop the wheel from reaching the snap driver
  galleries.forEach(g => g.addEventListener('wheel', e => e.stopPropagation(), { passive: true }))

  document.querySelectorAll('.gallery-item').forEach(item => {
    item.addEventListener('click', () => {
      if (!lightboxImg) return
      lightboxImg.src = item.querySelector('img').src
      charLightbox.classList.add('open')
    })
  })
  charLightbox?.addEventListener('click', closeLightbox)
  window.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return
    if (charLightbox?.classList.contains('open')) closeLightbox()
    else if (galleries.some(g => g.classList.contains('open'))) closeGalleries()
  })

  // ── Scroll phase thresholds ──────────────────────────────────────────────
  // Camera descent starts at DESCENT_START (before model1 rotation fully ends)
  // creating a natural overlap — the transition feels gradual, not snappy.
  // P1: 0 → P1_END      — model1 rotation locks (≈320°)
  // P2: DESCENT → P2_END — camera descends + zooms to face (starts early)
  // P3: P2_END → P3_END  — pull back to full model, model2 rotates
  const P1_END        = 1400  // model1 rotation cap
  const DESCENT_START = 800   // camera starts easing down from here
  const P2_END        = 1700  // face closeup fully arrived
  const P3_END        = 2800  // full model reveal complete
  const P4_END        = P3_END + 300  // pan to spread
  const P4D_END       = P4_END
  const P5_END        = 7000  // carousel fully scrolled through
  const P_ENV_END     = 8000  // Environments top-down snap
  const P_ANIM_END    = 9000   // Animation section snap
  const P_COLLECT_END = 10500  // Digital Collectibles snap
  const P_CONTACT_END = P_COLLECT_END + 1500   // Contact form — last section

  // ── Snap points — one per meaningful visual state ────────────────────────
  const CAR_RANGE = P5_END - P4_END
  // Mobile: extra snap inside characters section to step through each model 1-by-1
  // Desktop: unchanged (all 3 spread in one view)
  const MOB_CAR1  = 3500   // mobile snap 3 → Guardians centred
  // P5_END (7000) becomes mobile snap 4 → Axolotls centred
  const SNAP_POINTS = isMobile ? [
    0,                            // 0  Hero
    750,                          // 1  Our Studio / About
    P2_END,                       // 2  Face closeup (Duo)
    MOB_CAR1,                     // 3  Guardians
    P5_END,                       // 4  Axolotls
    P_ENV_END,                    // 5  Environments top-down
    P_ANIM_END,                   // 6  Animation showcase
    P_COLLECT_END,                // 7  Digital Collectibles
    P_CONTACT_END,                // 8  Contact
  ] : [
    0,                            // 0  Hero
    750,                          // 1  Our Studio / About
    P2_END,                       // 2  Face closeup + Characters text
    P5_END,                       // 3  All characters spread (no text)
    P_ENV_END,                    // 4  Environments top-down
    P_ANIM_END,                   // 5  Animation showcase
    P_COLLECT_END,                // 6  Digital Collectibles
    P_CONTACT_END,                // 7  Contact
  ]
  setSnaps(SNAP_POINTS)

  // Horizontal trackpad scroll → rotate the model under the cursor
  setHorizontalHandler(deltaX => {
    const key = getCachedHoverKey()
    if (key) applyRotDelta(key, -deltaX * 0.4)
  })

  // Dot click → navigate to that section
  const dotTargets = [0, 750, P2_END, P5_END, P_ENV_END, P_ANIM_END, P_COLLECT_END, P_CONTACT_END]
  dots.forEach((dot, i) => {
    dot.addEventListener('click', () => scrollTo(dotTargets[i]))
  })

  // Upper-body closeup: camera looks at upper chest/head area of the character
  const HEAD_OFFSET = 1.8

  let autoAngle  = 0.38        // model1 base angle
  let autoAngle2 = 0.0
  let prevScroll = 0           // previous frame scroll for delta
  let prevP3t    = 0           // tracks p3t transitions

  // ── LG about-section model ─────────────────────────────────────────────────
  const lgModels     = [lgPurple, lgYellow, lgGreen]   // declared here so init code below can use it
  const heroVariants = [heroYellow, heroRed]           // alternate hero skins — share 'm1' key
  let lgAngle = 0               // auto-rotate accumulator (base angle comes from lgParams.rotationY)
  let lgWasVisible         = false
  let model1WasVisible     = false
  let model6WasVisible     = false
  let companionsWasVisible = false
  let animWasVisible       = false
  let envWasVisible        = false
  let activeLGColor   = 'purple'  // 'purple' | 'yellow' | 'green'
  let activeHeroColor = 'purple'  // 'purple' | 'yellow' | 'red'

  // Hero skin picker click handler
  heroColorPicker.querySelectorAll('.hero-swatch').forEach(swatch => {
    swatch.addEventListener('click', () => {
      activeHeroColor = swatch.dataset.color
      heroColorPicker.querySelectorAll('.hero-swatch').forEach(s => {
        s.classList.toggle('active', s.dataset.color === activeHeroColor)
      })
    })
  })

  // ── LG GUI — position controls ────────────────────────────────────────────
  const LG_GUI_KEY = 'lg-editor-v16'
  const lgParams = {
    // 3D model position
    modelX:  isMobile ?  0.15 : -2.4,
    modelY:  isMobile ? -1.4  :  0.05,
    modelZ:  isMobile ?  0    :  1.5,
    // Base rotation
    rotationY: isMobile ? 12.0675 : -0.2006,
    // CSS color-picker position (% values)
    pickerLeft:   isMobile ? 50 : 43,
    pickerBottom: isMobile ? 8  : 41,
    // Rotate hint position (% values)
    hintLeft:   isMobile ? 50 : 53,
    hintBottom: isMobile ? 15 : 26,
    // Model scale (mobile only — desktop scale is handled by loadGLB)
    scale: 1.45,
  }
  // Restore saved values
  try {
    const saved = localStorage.getItem(LG_GUI_KEY)
    if (saved) Object.assign(lgParams, JSON.parse(saved))
  } catch {}

  const lgGui = new GUI({ title: 'LG Section ⠿ drag', width: 260 })
  lgGui.hide()
  const lgGuiEl = lgGui.domElement
  lgGuiEl.style.position = 'fixed'
  lgGuiEl.style.top  = '12px'
  lgGuiEl.style.right = '12px'
  lgGuiEl.style.zIndex = '9999'
  document.body.appendChild(lgGuiEl)
  // Draggable
  ;(function makeLgGuiDraggable() {
    const hdr = lgGuiEl.querySelector('.title'); if (!hdr) return
    let mx = 0, my = 0, dragging = false
    hdr.style.cursor = 'grab'
    hdr.addEventListener('mousedown', e => {
      dragging = true
      const rect = lgGuiEl.getBoundingClientRect()
      lgGuiEl.style.right = 'auto'; lgGuiEl.style.left = rect.left + 'px'
      mx = e.clientX - rect.left; my = e.clientY - rect.top
      hdr.style.cursor = 'grabbing'; e.preventDefault()
    })
    document.addEventListener('mousemove', e => {
      if (!dragging) return
      lgGuiEl.style.left = Math.max(0, e.clientX - mx) + 'px'
      lgGuiEl.style.top  = Math.max(0, e.clientY - my) + 'px'
    })
    document.addEventListener('mouseup', () => { dragging = false; hdr.style.cursor = 'grab' })
  })()

  const lgModelFolder = lgGui.addFolder('Model Position')
  lgModelFolder.add(lgParams, 'modelX', -5, 5, 0.05).name('X')
  lgModelFolder.add(lgParams, 'modelY', -3, 3, 0.05).name('Y')
  lgModelFolder.add(lgParams, 'modelZ', -5, 5, 0.05).name('Z')
  lgModelFolder.add(lgParams, 'scale', 0.5, 6, 0.05).name('Scale')
  const lgRotCtrl = lgModelFolder.add(lgParams, 'rotationY', -Math.PI * 2, Math.PI * 2, 0.001).name('Rotation Y')
  lgModelFolder.add({
    capture() {
      // Bake current visual rotation (base + auto-spin + drag) into rotationY,
      // then zero the accumulators so this becomes the new resting position.
      lgParams.rotationY = lgParams.rotationY + lgAngle + userRot.lg
      lgAngle        = 0
      userRot.lg     = 0
      rotVelocity.lg = 0
      lgRotCtrl.updateDisplay()
      console.log('📍 Start rotation captured:', lgParams.rotationY.toFixed(4))
    }
  }, 'capture').name('📍 Capture Current Angle as Start')

  const lgPickerFolder = lgGui.addFolder('Color Picker Position')
  lgPickerFolder.add(lgParams, 'pickerLeft',   0, 100, 1).name('Left %')
  lgPickerFolder.add(lgParams, 'pickerBottom', 0, 100, 1).name('Bottom %')

  const lgHintFolder = lgGui.addFolder('Rotate Hint Position')
  lgHintFolder.add(lgParams, 'hintLeft',   0, 100, 1).name('Left %')
  lgHintFolder.add(lgParams, 'hintBottom', 0, 100, 1).name('Bottom %')

  lgGui.add({
    save() {
      localStorage.setItem(LG_GUI_KEY, JSON.stringify(lgParams))
      const json = JSON.stringify(lgParams, null, 2)
      console.log('✅ LG values saved:\n' + json)
      // Show copyable overlay so values are accessible on mobile (no console)
      let ov = document.getElementById('_lgSaveOverlay')
      if (!ov) {
        ov = document.createElement('div')
        ov.id = '_lgSaveOverlay'
        Object.assign(ov.style, {
          position:'fixed', top:'0', left:'0', width:'100%', height:'100%',
          background:'rgba(0,0,0,0.88)', zIndex:'99999', display:'flex',
          flexDirection:'column', alignItems:'center', justifyContent:'center',
          padding:'20px', boxSizing:'border-box'
        })
        document.body.appendChild(ov)
      }
      ov.innerHTML = `
        <div style="color:#a78bfa;font-family:monospace;font-size:13px;margin-bottom:12px;font-weight:bold;">LG VALUES — paste to Claude</div>
        <textarea id="_lgSaveTxt" readonly style="width:100%;max-width:480px;height:260px;background:#1a0a2e;color:#e2e8f0;font-family:monospace;font-size:12px;border:1px solid #6d28d9;border-radius:6px;padding:10px;resize:none;">${json}</textarea>
        <button id="_lgSaveCopy" style="margin-top:12px;padding:10px 28px;background:#7c3aed;color:#fff;border:none;border-radius:6px;font-size:14px;cursor:pointer;">Copy</button>
        <button id="_lgSaveClose" style="margin-top:8px;padding:8px 24px;background:transparent;color:#a78bfa;border:1px solid #6d28d9;border-radius:6px;font-size:13px;cursor:pointer;">Close</button>`
      document.getElementById('_lgSaveCopy').onclick = () => {
        document.getElementById('_lgSaveTxt').select()
        document.execCommand('copy')
        document.getElementById('_lgSaveCopy').textContent = 'Copied ✓'
      }
      document.getElementById('_lgSaveClose').onclick = () => ov.remove()
    }
  }, 'save').name('💾 Save')
  lgGui.add({
    reset() { localStorage.removeItem(LG_GUI_KEY); location.reload() }
  }, 'reset').name('🗑️ Reset')

  lgModels.forEach(m => m.position.set(lgParams.modelX, lgParams.modelY, lgParams.modelZ))
  // Apply initial picker position before first frame
  lgColorPicker.style.left   = `${lgParams.pickerLeft}%`
  lgColorPicker.style.bottom = `${lgParams.pickerBottom}%`
  lgColorPicker.style.transform = lgParams.pickerLeft === 50 ? 'translateX(-50%)' : 'none'
  lgPurple.visible = true   // purple shown by default
  lgYellow.visible = false
  lgGreen.visible  = false

  // Cache all LG materials for opacity control (transparent = true from the start)
  const allLGMats = []
  lgModels.forEach(mdl => {
    mdl.traverse(child => {
      if (child.isMesh) {
        const mats = Array.isArray(child.material) ? child.material : [child.material]
        mats.forEach(m => {
          m.transparent = false
          m.opacity     = 1.0
          m.alphaTest   = 0
          m.depthWrite  = true
          m.side        = THREE.DoubleSide
          m.needsUpdate = true
          allLGMats.push(m)  // kept for any future use
        })
      }
    })
  })

  // Color picker click handler
  lgColorPicker.querySelectorAll('.lg-swatch').forEach(swatch => {
    swatch.addEventListener('click', () => {
      activeLGColor = swatch.dataset.color
      lgColorPicker.querySelectorAll('.lg-swatch').forEach(s => {
        s.classList.toggle('active', s.dataset.color === activeLGColor)
      })
    })
  })

  // ── Carousel state ────────────────────────────────────────────────────────
  let targetCarX       = 0
  let smoothCarX       = 0
  let carDragActive    = false
  let carDragStartX    = 0
  let carDragStartTarget = 0
  let inCarouselSection  = false
  const MAX_CAR_X      = 2.0   // centre camera on middle of 3 groups (x=0, 2, 4)

  const carCanvas = renderer.domElement
  carCanvas.addEventListener('mousedown', e => {
    if (!inCarouselSection) return
    carDragActive = true; carDragStartX = e.clientX; carDragStartTarget = targetCarX
    e.preventDefault()
  })
  window.addEventListener('mousemove', e => {
    if (!carDragActive) return
    const dx = (e.clientX - carDragStartX) * 8 / window.innerWidth
    targetCarX = Math.min(Math.max(carDragStartTarget - dx, 0), MAX_CAR_X)
  })
  window.addEventListener('mouseup', () => { carDragActive = false })

  let initCarTouchX = null, initCarTouchY = null, lastCarTouchX = null, carTouchDir = null
  carCanvas.addEventListener('touchstart', e => {
    if (!inCarouselSection || e.touches.length !== 1) return
    initCarTouchX = lastCarTouchX = e.touches[0].clientX
    initCarTouchY = e.touches[0].clientY; carTouchDir = null
  }, { passive: true })
  window.addEventListener('touchmove', e => {
    if (!inCarouselSection || lastCarTouchX === null || e.touches.length !== 1) return
    const t = e.touches[0]
    if (carTouchDir === null) {
      const dx = Math.abs(t.clientX - initCarTouchX), dy = Math.abs(t.clientY - initCarTouchY)
      if (dx > 8 || dy > 8) carTouchDir = dx > dy ? 'h' : 'v'
    }
    if (carTouchDir === 'h') {
      const dx = (lastCarTouchX - t.clientX) * 8 / window.innerWidth
      targetCarX = Math.min(Math.max(targetCarX + dx, 0), MAX_CAR_X)
    }
    lastCarTouchX = t.clientX
  }, { passive: true })
  window.addEventListener('touchend', () => { lastCarTouchX = null; carTouchDir = null }, { passive: true })

  // ── Per-model drag rotation ────────────────────────────────────────────────
  const userRot     = { m1: 0, m3: 0, m4: 0, m6: 0, ma: 0, me: 0, lg: 0 }
  const rotVelocity = { m1: 0, m3: 0, m4: 0, m6: 0, ma: 0, me: 0, lg: 0 }
  const modelMap    = [
    { key: 'm1', obj: model1 }, { key: 'm6', obj: model6 },
    { key: 'm4', obj: model4 }, { key: 'm3', obj: model3 },
    { key: 'ma', obj: animModel },
  ]
  const raycaster  = new THREE.Raycaster()
  const mouseNDC   = new THREE.Vector2()
  let lastMouseX   = window.innerWidth  / 2
  let lastMouseY   = window.innerHeight / 2
  let mouseDirty      = true   // true whenever mouse position changed since last raycast
  let cachedHoverKey  = null   // last raycast result — reused until mouse moves

  // Cast a ray and return the key of the first model hit
  function hitTestKey(cx, cy) {
    mouseNDC.x =  (cx / window.innerWidth)  * 2 - 1
    mouseNDC.y = -(cy / window.innerHeight) * 2 + 1
    raycaster.setFromCamera(mouseNDC, camera)
    for (const { key, obj } of modelMap) {
      if (raycaster.intersectObject(obj, true).length > 0) return key
    }
    // LG about-section models (checked separately to avoid velocity loop issues)
    for (const mdl of lgModels) {
      if (mdl.visible && raycaster.intersectObject(mdl, true).length > 0) return 'lg'
    }
    // Hero skin variants — same drag key as model1
    for (const mdl of heroVariants) {
      if (mdl.visible && raycaster.intersectObject(mdl, true).length > 0) return 'm1'
    }
    return null
  }

  // Return cached raycast result — only re-casts when mouse has moved
  function getCachedHoverKey() {
    if (mouseDirty) { cachedHoverKey = hitTestKey(lastMouseX, lastMouseY); mouseDirty = false }
    return cachedHoverKey
  }

  // Apply a rotation delta to a model key + update velocity for momentum
  const SENS = Math.PI * 2.5  // radians per full viewport width drag
  function applyRotDelta(key, dx) {
    const delta = dx * SENS / window.innerWidth
    rotVelocity[key] = rotVelocity[key] * 0.5 + delta * 0.5   // smooth velocity
    userRot[key] += delta
  }

  let modelDragActive = false
  let modelDragKey    = null
  let modelDragLastX  = 0

  // Desktop — mousedown / mousemove / mouseup
  carCanvas.addEventListener('mousedown', e => {
    const key = hitTestKey(e.clientX, e.clientY)
    if (!key) return
    modelDragActive      = true
    modelDragKey         = key
    modelDragLastX       = e.clientX
    rotVelocity[key]     = 0
    carCanvas.style.cursor = 'grabbing'
    e.preventDefault()
  }, { capture: true })

  window.addEventListener('mousemove', e => {
    lastMouseX = e.clientX; lastMouseY = e.clientY
    mouseDirty = true
    if (modelDragActive && modelDragKey) {
      applyRotDelta(modelDragKey, e.clientX - modelDragLastX)
      modelDragLastX = e.clientX
    }
  })

  window.addEventListener('mouseup', () => {
    modelDragActive = false
    // velocity stays — momentum will carry it
  })

  // Mobile — touchstart / touchmove / touchend
  // Only rotate when gesture is clearly horizontal (|dx| > |dy|) to avoid
  // accidental rotation during vertical section-swipes.
  let modelTouchLastX  = null
  let modelTouchLastY  = null
  let modelTouchKey    = null
  let modelTouchLocked = null  // 'h' = horizontal rotate, 'v' = vertical scroll, null = undecided
  const MOB_ROT_LOCK_PX = 12   // px of movement before we decide gesture direction

  carCanvas.addEventListener('touchstart', e => {
    if (e.touches.length !== 1) return
    const t   = e.touches[0]
    const key = hitTestKey(t.clientX, t.clientY)
    if (!key) return
    modelTouchKey    = key
    modelTouchLastX  = t.clientX
    modelTouchLastY  = t.clientY
    modelTouchLocked = null
    rotVelocity[key] = 0
  }, { passive: true })

  window.addEventListener('touchmove', e => {
    if (!modelTouchKey || modelTouchLastX === null || e.touches.length !== 1) return
    const dx = e.touches[0].clientX - modelTouchLastX
    const dy = e.touches[0].clientY - modelTouchLastY

    // Determine gesture direction on first significant movement
    if (modelTouchLocked === null) {
      const dist = Math.sqrt(dx * dx + dy * dy)
      if (dist < MOB_ROT_LOCK_PX) return   // not enough movement yet to decide
      modelTouchLocked = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v'
    }

    modelTouchLastX = e.touches[0].clientX
    modelTouchLastY = e.touches[0].clientY

    if (modelTouchLocked === 'h') applyRotDelta(modelTouchKey, dx)
  }, { passive: true })

  window.addEventListener('touchend', () => {
    modelTouchLastX  = null
    modelTouchLastY  = null
    modelTouchLocked = null
    // keep modelTouchKey + velocity for momentum
  }, { passive: true })

  const model1BaseY = window.innerWidth <= 640 ? 0.3 : 0
  const model2BaseY = -sectionGap

  // Cache model1 materials so we can fade it out during the About section
  const model1Mats = []
  model1.traverse(child => {
    if (child.isMesh) {
      const mats = Array.isArray(child.material) ? child.material : [child.material]
      mats.forEach(m => { m.transparent = true; model1Mats.push(m) })
    }
  })

  // Hero skin variants — permanently opaque (no mix-blend transparency issues)
  const heroYellowMats = [], heroRedMats = []
  ;[{ mdl: heroYellow, arr: heroYellowMats }, { mdl: heroRed, arr: heroRedMats }].forEach(({ mdl, arr }) => {
    mdl.traverse(child => {
      if (!child.isMesh) return
      const mats = Array.isArray(child.material) ? child.material : [child.material]
      mats.forEach(m => {
        m.transparent = true   // needed for fade in/out like model1
        m.side        = THREE.DoubleSide
        m.needsUpdate = true
        arr.push(m)
      })
    })
    mdl.visible = false
  })

  // Companions: visibility-only toggle — no material opacity to avoid bleed-through
  const companionMats = []

  // model6 (duo): visibility-only toggle — no material opacity fade to avoid
  // clothing meshes bleeding through each other when semi-transparent
  // model6HeroMats kept as empty array so opacity lines below are no-ops
  const model6HeroMats = []

  // Cache env model materials for fade in/out.
  // Do NOT globally set transparent=true — that pushes all materials into the
  // transparent render queue and causes bloom to behave differently for other models.
  // Instead, toggle transparent dynamically in the animate loop (only when fading).
  const envMats = []
  const maxAniso = renderer.capabilities.getMaxAnisotropy()
  const TEX_SLOTS = ['map','normalMap','roughnessMap','metalnessMap','aoMap','emissiveMap','alphaMap']
  envModel.traverse(child => {
    if (child.isMesh) {
      const mats = Array.isArray(child.material) ? child.material : [child.material]
      mats.forEach(m => {
        m._envHasAlpha = !!m.alphaMap   // Plane.001 gets alphaMap from loadEnvGLB
        envMats.push(m)
        // DoubleSide: leaf/foliage planes have normals on one side only — without this
        // the shadow light hitting backfaces produces black patches on the canopy.
        m.side = THREE.DoubleSide
        // Disable normal maps — under a strong directional light the perturbed normals
        // flip some leaf faces away from the light, creating harsh dark patches.
        if (m.normalMap) { m.normalMap = null }
        if (m.normalScale) m.normalScale.set(0, 0)
        m.needsUpdate = true
        // Max anisotropic filtering — critical for sharp textures at oblique iso angles
        TEX_SLOTS.forEach(slot => {
          if (m[slot]) { m[slot].anisotropy = maxAniso; m[slot].needsUpdate = true }
        })
      })
    }
  })

  // ── Apply procedural warm studio envMap to every env material ──────────────
  // Uses material.envMap (per-material), NOT scene.environment, so other models
  // in the site are completely unaffected by this warm HDRI.
  // Initial envMap — built with the hardcoded sun defaults (not the old getEnvMap)
  const initialEnvMap = generateEnvMap(renderer, {
    sunX: 0.37, sunY: 0.28, sunRadius: 0.30, sunBright: 1.15,
    sunR: 255, sunG: 230, sunB: 160,
    ambientR: 8, ambientG: 8, ambientB: 16,
    groundBounce: 0.25, skyFill: 0.15,
  })
  envMats.forEach(m => {
    if (m.isMeshStandardMaterial) {
      m.envMap = initialEnvMap
      m.envMapIntensity = 5.6
      m.needsUpdate = true
    }
  })

  // ── Debug GUI — lil-gui panel for real-time env tuning ──────────────────────
  // Remove or comment out this block for production.
  const gui = new GUI({ title: 'Environment Editor ⠿ drag to move', width: 320 })
  gui.hide()
  const guiEl = gui.domElement
  guiEl.style.zIndex = '9999'
  guiEl.style.position = 'fixed'
  guiEl.style.top = '0px'
  guiEl.style.right = '0px'
  guiEl.style.left = 'auto'

  // Make the GUI panel draggable by its title bar
  ;(function makeDraggable() {
    // lil-gui title is the first direct child of the root element
    const titleBar = guiEl.children[0]
    if (!titleBar) return
    titleBar.style.cursor = 'grab'
    titleBar.style.userSelect = 'none'
    let dragging = false, offX = 0, offY = 0
    titleBar.addEventListener('pointerdown', e => {
      dragging = true
      titleBar.style.cursor = 'grabbing'
      const rect = guiEl.getBoundingClientRect()
      // Switch from right-anchored to left-anchored positioning
      guiEl.style.right = 'auto'
      guiEl.style.left = rect.left + 'px'
      guiEl.style.top = rect.top + 'px'
      offX = e.clientX - rect.left
      offY = e.clientY - rect.top
      titleBar.setPointerCapture(e.pointerId)
      e.preventDefault()
      e.stopPropagation()
    })
    titleBar.addEventListener('pointermove', e => {
      if (!dragging) return
      // Clamp so the panel can never be dragged fully off-screen
      const maxX = window.innerWidth  - guiEl.offsetWidth
      const maxY = window.innerHeight - guiEl.children[0].offsetHeight  // at least title stays visible
      guiEl.style.left = Math.max(0, Math.min(maxX, e.clientX - offX)) + 'px'
      guiEl.style.top  = Math.max(0, Math.min(maxY, e.clientY - offY)) + 'px'
    })
    titleBar.addEventListener('pointerup', e => {
      if (!dragging) return
      dragging = false
      titleBar.style.cursor = 'grab'
      titleBar.releasePointerCapture(e.pointerId)
    })
  })()

  // Helper: regenerate envMap and apply to all env materials
  function rebuildEnvMap() {
    const map = generateEnvMap(renderer, sunParams)
    envMats.forEach(m => {
      if (m.isMeshStandardMaterial) { m.envMap = map; m.needsUpdate = true }
    })
  }

  // ── Model transform ───────────────────────────────────────────────────────
  const modelFolder = gui.addFolder('Model Transform')
  const modelParams = {
    posX: 0, posY: -6, posZ: 0,
    rotY: 2.616946,
    scale: 16,
  }
  modelFolder.add(modelParams, 'posX', -10, 10, 0.1).name('Position X').onChange(v => { envModel.position.x = v })
  modelFolder.add(modelParams, 'posY', -15, 0, 0.1).name('Position Y').onChange(v => { envModel.position.y = v })
  modelFolder.add(modelParams, 'posZ', -10, 10, 0.1).name('Position Z').onChange(v => { envModel.position.z = v })
  modelFolder.add(modelParams, 'rotY', 0, Math.PI * 2, 0.01).name('Rotation Y')
  // scale is read-only info for now

  // ── EnvMap sun parameters ─────────────────────────────────────────────────
  const sunFolder = gui.addFolder('EnvMap Lighting')
  const sunParams = {
    sunX: 0.37, sunY: 0.28, sunRadius: 0.30, sunBright: 1.15,
    sunR: 255, sunG: 230, sunB: 160,
    ambientR: 8, ambientG: 8, ambientB: 16,
    groundBounce: 0.25, skyFill: 0.15,
    envMapIntensity: 5.6,
  }
  sunFolder.add(sunParams, 'sunX', 0, 1, 0.01).name('Sun Horizontal').onChange(rebuildEnvMap)
  sunFolder.add(sunParams, 'sunY', 0, 1, 0.01).name('Sun Vertical').onChange(rebuildEnvMap)
  sunFolder.add(sunParams, 'sunRadius', 0.05, 0.8, 0.01).name('Sun Size').onChange(rebuildEnvMap)
  sunFolder.add(sunParams, 'sunBright', 0, 2, 0.05).name('Sun Brightness').onChange(rebuildEnvMap)
  sunFolder.add(sunParams, 'sunR', 0, 255, 1).name('Sun Red').onChange(rebuildEnvMap)
  sunFolder.add(sunParams, 'sunG', 0, 255, 1).name('Sun Green').onChange(rebuildEnvMap)
  sunFolder.add(sunParams, 'sunB', 0, 255, 1).name('Sun Blue').onChange(rebuildEnvMap)
  sunFolder.add(sunParams, 'ambientR', 0, 60, 1).name('Ambient R').onChange(rebuildEnvMap)
  sunFolder.add(sunParams, 'ambientG', 0, 60, 1).name('Ambient G').onChange(rebuildEnvMap)
  sunFolder.add(sunParams, 'ambientB', 0, 60, 1).name('Ambient B').onChange(rebuildEnvMap)
  sunFolder.add(sunParams, 'groundBounce', 0, 1, 0.01).name('Ground Bounce').onChange(rebuildEnvMap)
  sunFolder.add(sunParams, 'skyFill', 0, 0.5, 0.01).name('Sky Fill').onChange(rebuildEnvMap)
  sunFolder.add(sunParams, 'envMapIntensity', 0, 10, 0.1).name('EnvMap Intensity').onChange(v => {
    envMats.forEach(m => { if (m.isMeshStandardMaterial) m.envMapIntensity = v })
  })

  // ── Global light muting ────────────────────────────────────────────────
  const muteFolder = gui.addFolder('Global Light Mute')
  const muteParams = { muteGlobals: 0.81 }
  muteFolder.add(muteParams, 'muteGlobals', 0, 1, 0.01).name('Mute Amount (0=off, 1=full)')
    .onChange(v => { window.__envMuteGlobals = v })
  window.__envMuteGlobals = 0.81  // default: partially mute globals during env

  // ── Shadow light ──────────────────────────────────────────────────────────
  if (envShadowLight) {
    const slFolder = gui.addFolder('Shadow Light')
    const slParams = {
      intensity: 5.1, color: '#ffe0a0',
      posX: -10, posY: 2.5, posZ: 8,
    }
    slFolder.add(slParams, 'intensity', 0, 8, 0.1).name('Intensity')
    slFolder.addColor(slParams, 'color').name('Color').onChange(v => envShadowLight.color.set(v))
    slFolder.add(slParams, 'posX', -15, 15, 0.5).name('Pos X').onChange(v => { envShadowLight.position.x = v })
    slFolder.add(slParams, 'posY', -10, 10, 0.5).name('Pos Y').onChange(v => { envShadowLight.position.y = v })
    slFolder.add(slParams, 'posZ', -15, 15, 0.5).name('Pos Z').onChange(v => { envShadowLight.position.z = v })
    window.__envShadowParams = slParams
  }

  // ── Camera ────────────────────────────────────────────────────────────────
  const camFolder = gui.addFolder('Camera (Env)')
  const camParams = { lookX: -4.8, lookYOffset: -3.0, camY: 0.7, camZ: 14.5 }
  camFolder.add(camParams, 'lookX', -15, 0, 0.1).name('Look X (right)')
  camFolder.add(camParams, 'lookYOffset', -10, 5, 0.1).name('Look Y offset')
  camFolder.add(camParams, 'camY', 0, 15, 0.1).name('Cam Height')
  camFolder.add(camParams, 'camZ', 5, 30, 0.5).name('Cam Distance')

  // ── Materials ─────────────────────────────────────────────────────────────
  const matFolder = gui.addFolder('Materials')
  const matParams = { roughness: 0.65, metalness: 0.0 }
  matFolder.add(matParams, 'roughness', 0, 1, 0.01).name('Roughness').onChange(v => {
    envMats.forEach(m => { if (m.isMeshStandardMaterial) { m.roughness = v; m.needsUpdate = true } })
  })
  matFolder.add(matParams, 'metalness', 0, 1, 0.01).name('Metalness').onChange(v => {
    envMats.forEach(m => { if (m.isMeshStandardMaterial) { m.metalness = v; m.needsUpdate = true } })
  })

  // ── Log values button ─────────────────────────────────────────────────────
  gui.add({ logValues() {
    console.log('=== ENV EDITOR VALUES ===')
    console.log('Model:', JSON.stringify(modelParams, null, 2))
    console.log('Sun:', JSON.stringify(sunParams, null, 2))
    console.log('Shadow:', JSON.stringify(window.__envShadowParams, null, 2))
    console.log('Camera:', JSON.stringify(camParams, null, 2))
    console.log('Materials:', JSON.stringify(matParams, null, 2))
    console.log('=========================')
  }}, 'logValues').name('📋 Log Values to Console')

  // ── LocalStorage persistence — auto-save/restore GUI values ────────────────
  const STORAGE_KEY = 'env-editor-settings-v3'
  function saveToStorage() {
    const data = { modelParams, sunParams, camParams, matParams, muteParams }
    if (window.__envShadowParams) data.slParams = { ...window.__envShadowParams }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  }
  function loadFromStorage() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return false
      const data = JSON.parse(raw)
      if (data.modelParams) Object.assign(modelParams, data.modelParams)
      if (data.sunParams) Object.assign(sunParams, data.sunParams)
      if (data.camParams) Object.assign(camParams, data.camParams)
      if (data.matParams) Object.assign(matParams, data.matParams)
      if (data.muteParams) Object.assign(muteParams, data.muteParams)
      if (data.slParams && window.__envShadowParams) Object.assign(window.__envShadowParams, data.slParams)
      return true
    } catch { return false }
  }
  // Restore saved settings
  if (loadFromStorage()) {
    // Apply restored values
    envModel.position.set(modelParams.posX, modelParams.posY, modelParams.posZ)
    rebuildEnvMap()
    envMats.forEach(m => {
      if (m.isMeshStandardMaterial) {
        m.envMapIntensity = sunParams.envMapIntensity
        m.roughness = matParams.roughness
        m.metalness = matParams.metalness
        m.needsUpdate = true
      }
    })
    if (envShadowLight && window.__envShadowParams) {
      envShadowLight.color.set(window.__envShadowParams.color)
      envShadowLight.position.set(window.__envShadowParams.posX, window.__envShadowParams.posY, window.__envShadowParams.posZ)
    }
    window.__envMuteGlobals = muteParams.muteGlobals
    gui.controllersRecursive().forEach(c => c.updateDisplay())
    console.log('✅ Restored env editor settings from localStorage')
  }
  // Auto-save on every change
  gui.onChange(saveToStorage)

  // Save + Reset buttons
  gui.add({ saveSettings() {
    saveToStorage()
    console.log('✅ Settings saved to localStorage')
  }}, 'saveSettings').name('💾 Save Settings')
  gui.add({ resetSettings() {
    localStorage.removeItem(STORAGE_KEY)
    location.reload()
  }}, 'resetSettings').name('🗑️ Reset to Defaults')

  // ── Unlimited Undo (Ctrl+Z) ───────────────────────────────────────────────
  const undoHistory = []
  const allParams = { modelParams, sunParams, camParams, matParams, muteParams }
  if (window.__envShadowParams) allParams.slParams = window.__envShadowParams

  // Snapshot current state
  function pushUndo() {
    const snap = {}
    for (const [key, obj] of Object.entries(allParams)) {
      snap[key] = { ...obj }
    }
    undoHistory.push(snap)
  }

  // Record initial state
  pushUndo()

  // Listen to ALL gui changes — push undo before every change
  gui.onChange(() => {
    // Only push if the last snapshot differs (avoid duplicates from rapid slider moves)
    const last = undoHistory[undoHistory.length - 1]
    let differs = false
    for (const [key, obj] of Object.entries(allParams)) {
      for (const prop in obj) {
        if (obj[prop] !== last[key]?.[prop]) { differs = true; break }
      }
      if (differs) break
    }
    if (differs) pushUndo()
  })

  // Ctrl+Z handler
  window.addEventListener('keydown', (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
      e.preventDefault()
      if (undoHistory.length <= 1) return  // nothing to undo
      undoHistory.pop()  // remove current state
      const prev = undoHistory[undoHistory.length - 1]

      // Restore all param objects
      for (const [key, obj] of Object.entries(allParams)) {
        if (prev[key]) Object.assign(obj, prev[key])
      }

      // Apply restored values
      // Model
      envModel.position.set(modelParams.posX, modelParams.posY, modelParams.posZ)
      // Sun / envMap
      rebuildEnvMap()
      envMats.forEach(m => {
        if (m.isMeshStandardMaterial) {
          m.envMapIntensity = sunParams.envMapIntensity
          m.roughness = matParams.roughness
          m.metalness = matParams.metalness
          m.needsUpdate = true
        }
      })
      // Shadow light
      if (envShadowLight && allParams.slParams) {
        envShadowLight.color.set(allParams.slParams.color)
        envShadowLight.position.set(allParams.slParams.posX, allParams.slParams.posY, allParams.slParams.posZ)
      }
      // Mute
      window.__envMuteGlobals = muteParams.muteGlobals

      // Update GUI controllers to show restored values
      gui.controllersRecursive().forEach(c => c.updateDisplay())
    }
  })

  // Export for animate loop
  window.__envCamParams = camParams
  window.__envModelParams = modelParams


  // ── Animation Model GUI ───────────────────────────────────────────────────
  const animGui = new GUI({ title: 'Animation Editor ⠿ drag to move', width: 280 })
  animGui.hide()
  const animGuiEl = animGui.domElement
  animGuiEl.style.zIndex = '9999'
  animGuiEl.style.position = 'fixed'
  animGuiEl.style.top = '0px'
  animGuiEl.style.left = '0px'

  // Draggable title bar
  ;(function makeAnimDraggable() {
    const titleBar = animGuiEl.children[0]
    if (!titleBar) return
    titleBar.style.cursor = 'grab'
    titleBar.style.userSelect = 'none'
    let dragging = false, offX = 0, offY = 0
    titleBar.addEventListener('pointerdown', e => {
      dragging = true; titleBar.style.cursor = 'grabbing'
      const rect = animGuiEl.getBoundingClientRect()
      animGuiEl.style.left = rect.left + 'px'; animGuiEl.style.top = rect.top + 'px'
      offX = e.clientX - rect.left; offY = e.clientY - rect.top
      titleBar.setPointerCapture(e.pointerId); e.preventDefault(); e.stopPropagation()
    })
    titleBar.addEventListener('pointermove', e => {
      if (!dragging) return
      const maxX = window.innerWidth - animGuiEl.offsetWidth
      const maxY = window.innerHeight - animGuiEl.children[0].offsetHeight
      animGuiEl.style.left = Math.max(0, Math.min(maxX, e.clientX - offX)) + 'px'
      animGuiEl.style.top  = Math.max(0, Math.min(maxY, e.clientY - offY)) + 'px'
    })
    titleBar.addEventListener('pointerup', e => {
      if (!dragging) return; dragging = false
      titleBar.style.cursor = 'grab'; titleBar.releasePointerCapture(e.pointerId)
    })
  })()

  // ── Model transform ───────────────────────────────────────────────────────
  const animModelFolder = animGui.addFolder('Model')
  const animModelParams = { offsetX: 0.45, offsetY: 0, offsetZ: 0, rotY: 0.85, scale: 1.0 }
  animModelFolder.add(animModelParams, 'offsetX', -5, 5, 0.05).name('Position X')
  animModelFolder.add(animModelParams, 'offsetY', -3, 3, 0.05).name('Position Y offset')
  animModelFolder.add(animModelParams, 'offsetZ', -5, 5, 0.05).name('Position Z')
  animModelFolder.add(animModelParams, 'rotY', 0, Math.PI * 2, 0.01).name('Rotation Y base')
  animModelFolder.add(animModelParams, 'scale', 0.3, 3.0, 0.05).name('Scale').onChange(v => {
    animModel.scale.setScalar(v)
  })

  // ── Camera ────────────────────────────────────────────────────────────────
  const animCamFolder = animGui.addFolder('Camera')
  const animCamParams = { camYOffset: 1.1, camZ: 7.8, camXOffset: 3.2 }
  animCamFolder.add(animCamParams, 'camYOffset', -2, 6, 0.05).name('Cam Y offset')
  animCamFolder.add(animCamParams, 'camZ', 3, 20, 0.1).name('Cam Distance (Z)')
  animCamFolder.add(animCamParams, 'camXOffset', -5, 8, 0.05).name('Cam X pan')

  // ── Log / Save / Reset ────────────────────────────────────────────────────
  animGui.add({ log() {
    console.log('=== ANIM EDITOR VALUES ===')
    console.log('Model:', JSON.stringify(animModelParams, null, 2))
    console.log('Camera:', JSON.stringify(animCamParams, null, 2))
    console.log('==========================')
  }}, 'log').name('📋 Log Values to Console')

  const ANIM_STORAGE_KEY = 'anim-editor-settings-v2'
  function saveAnimToStorage() {
    localStorage.setItem(ANIM_STORAGE_KEY, JSON.stringify({ animModelParams, animCamParams }))
  }
  function loadAnimFromStorage() {
    try {
      const raw = localStorage.getItem(ANIM_STORAGE_KEY)
      if (!raw) return false
      const data = JSON.parse(raw)
      if (data.animModelParams) Object.assign(animModelParams, data.animModelParams)
      if (data.animCamParams)   Object.assign(animCamParams,   data.animCamParams)
      return true
    } catch { return false }
  }
  if (loadAnimFromStorage()) {
    animModel.scale.setScalar(animModelParams.scale)
    animGui.controllersRecursive().forEach(c => c.updateDisplay())
    console.log('✅ Restored anim editor settings from localStorage')
  }
  animGui.onChange(saveAnimToStorage)

  animGui.add({ save() {
    saveAnimToStorage()
    console.log('✅ Anim settings saved')
  }}, 'save').name('💾 Save Settings')
  animGui.add({ reset() {
    localStorage.removeItem(ANIM_STORAGE_KEY)
    location.reload()
  }}, 'reset').name('🗑️ Reset to Defaults')

  window.__animGuiParams = { model: animModelParams, cam: animCamParams }

  // ── Characters Model GUI ──────────────────────────────────────────────────
  const charGui = new GUI({ title: 'Characters Editor ⠿ drag to move', width: 280 })
  charGui.hide()
  const charGuiEl = charGui.domElement
  charGuiEl.style.zIndex = '9999'
  charGuiEl.style.position = 'fixed'
  charGuiEl.style.top = '0px'
  charGuiEl.style.right = '0px'
  charGuiEl.style.left = 'auto'

  ;(function makeCharDraggable() {
    const titleBar = charGuiEl.children[0]
    if (!titleBar) return
    titleBar.style.cursor = 'grab'
    let dragging = false, ox = 0, oy = 0
    titleBar.addEventListener('pointerdown', e => {
      dragging = true; titleBar.style.cursor = 'grabbing'
      const rect = charGuiEl.getBoundingClientRect()
      charGuiEl.style.right = 'auto'
      charGuiEl.style.left = rect.left + 'px'; charGuiEl.style.top = rect.top + 'px'
      ox = e.clientX - rect.left; oy = e.clientY - rect.top
      e.preventDefault()
    })
    window.addEventListener('pointermove', e => {
      if (!dragging) return
      charGuiEl.style.left = (e.clientX - ox) + 'px'
      charGuiEl.style.top  = (e.clientY - oy) + 'px'
    })
    window.addEventListener('pointerup', () => { dragging = false; titleBar.style.cursor = 'grab' })
  })()

  const CHAR_STORAGE_KEY = 'char-editor-settings-v8'
  const charParams = {
    duoOffsetY:      0,     duoOffsetX:  0,   duoOffsetZ:  0,   duoRotY:       0.42,
    guardianOffsetY: -0.15, guardianOffsetX: 0, guardianOffsetZ: 0, guardianRotY:  0.0584,
    axolotlOffsetY:  0,     axolotlOffsetX:  0, axolotlOffsetZ:  0, axolotlRotY:   0.4984,
    // Hero skin picker — desktop position (% from right / top)
    heroPickerRight: 10.5,
    heroPickerTop:   50,
  }

  const sg = sectionGap   // = 6, alias for use in closures below

  // Base positions match what's baked into scene.js — offsets are relative to those
  const BASE6X = -0.20, BASE4X = 2.15, BASE3X = 4.25

  const duoFolder = charGui.addFolder('Duo (left)')
  duoFolder.add(charParams, 'duoRotY',     -Math.PI, Math.PI, 0.01).name('Rotation Y')
  duoFolder.add(charParams, 'duoOffsetX',  -3, 3, 0.05).name('X offset').onChange(v => { model6.position.x = BASE6X + v })
  duoFolder.add(charParams, 'duoOffsetY',  -5, 5, 0.05).name('Y offset').onChange(v => { model6.position.y = -sg - 0.50 + v })
  duoFolder.add(charParams, 'duoOffsetZ',  -3, 3, 0.05).name('Z offset').onChange(v => { model6.position.z = v })

  const guardianFolder = charGui.addFolder('Guardians (centre)')
  guardianFolder.close()
  guardianFolder.add(charParams, 'guardianRotY',     -Math.PI, Math.PI, 0.01).name('Rotation Y')
  guardianFolder.add(charParams, 'guardianOffsetX',  -3, 3, 0.05).name('X offset').onChange(v => { model4.position.x = BASE4X + v })
  guardianFolder.add(charParams, 'guardianOffsetY',  -5, 5, 0.05).name('Y offset').onChange(v => { model4.position.y = -sg + 0.05 + v })
  guardianFolder.add(charParams, 'guardianOffsetZ',  -3, 3, 0.05).name('Z offset').onChange(v => { model4.position.z = v })

  const axolotlFolder = charGui.addFolder('Axolotls (right)')
  axolotlFolder.add(charParams, 'axolotlRotY',     -Math.PI, Math.PI, 0.01).name('Rotation Y')
  axolotlFolder.add(charParams, 'axolotlOffsetX',  -3, 3, 0.05).name('X offset').onChange(v => { model3.position.x = BASE3X + v })
  axolotlFolder.add(charParams, 'axolotlOffsetY',  -5, 5, 0.05).name('Y offset').onChange(v => { model3.position.y = -sg + v })
  axolotlFolder.add(charParams, 'axolotlOffsetZ',  -3, 3, 0.05).name('Z offset').onChange(v => { model3.position.z = v })

  const heroPickerFolder = charGui.addFolder('Hero Skin Picker (desktop)')
  heroPickerFolder.add(charParams, 'heroPickerRight', 0, 20, 0.5).name('Right %')
  heroPickerFolder.add(charParams, 'heroPickerTop',   0, 100, 1).name('Top %')
  heroPickerFolder.open()


  function saveCharToStorage() { localStorage.setItem(CHAR_STORAGE_KEY, JSON.stringify(charParams)) }
  function loadCharFromStorage() {
    try {
      const s = localStorage.getItem(CHAR_STORAGE_KEY)
      if (!s) return false
      Object.assign(charParams, JSON.parse(s))
      model6.position.set(BASE6X + charParams.duoOffsetX,      -sg - 0.50 + charParams.duoOffsetY,      charParams.duoOffsetZ)
      model4.position.set(BASE4X + charParams.guardianOffsetX, -sg + 0.05 + charParams.guardianOffsetY, charParams.guardianOffsetZ)
      model3.position.set(BASE3X + charParams.axolotlOffsetX,  -sg + charParams.axolotlOffsetY,         charParams.axolotlOffsetZ)
      return true
    } catch { return false }
  }

  if (loadCharFromStorage()) {
    charGui.controllersRecursive().forEach(c => c.updateDisplay())
  }
  charGui.onChange(saveCharToStorage)

  charGui.add({ save() {
    localStorage.setItem(CHAR_STORAGE_KEY, JSON.stringify(charParams))
    const json = JSON.stringify(charParams, null, 2)
    console.log('✅ CHAR values saved:\n' + json)
    let ov = document.getElementById('_charSaveOverlay')
    if (!ov) {
      ov = document.createElement('div')
      ov.id = '_charSaveOverlay'
      ov.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.85);z-index:99999;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;padding:24px;box-sizing:border-box;'
      document.body.appendChild(ov)
    }
    ov.innerHTML = `
      <div style="color:#fff;font-family:monospace;font-size:14px;margin-bottom:4px;">CHAR VALUES — paste to Claude</div>
      <textarea id="_charSaveTxt" readonly style="width:100%;max-width:420px;height:260px;background:#111;color:#7fff7f;font-family:monospace;font-size:12px;border:1px solid #444;padding:8px;border-radius:6px;resize:none;">${json}</textarea>
      <div style="display:flex;gap:12px;">
        <button id="_charSaveCopy" style="padding:10px 24px;background:#7c3aed;color:#fff;border:none;border-radius:6px;font-size:14px;cursor:pointer;">Copy</button>
        <button id="_charSaveClose" style="padding:10px 24px;background:#333;color:#fff;border:none;border-radius:6px;font-size:14px;cursor:pointer;">Close</button>
      </div>`
    document.getElementById('_charSaveCopy').onclick = () => {
      document.getElementById('_charSaveTxt').select()
      document.execCommand('copy')
      document.getElementById('_charSaveCopy').textContent = '✅ Copied!'
    }
    document.getElementById('_charSaveClose').onclick = () => ov.remove()
  }}, 'save').name('💾 Save')
  charGui.add({ reset() {
    localStorage.removeItem(CHAR_STORAGE_KEY)
    location.reload()
  }}, 'reset').name('🗑️ Reset to Defaults')

  // ── Face Closeup Camera GUI ────────────────────────────────────────────────
  const FACE_CAM_KEY = 'face-cam-settings-v2'
  const faceCamParams = {
    headOffset: 1.65,  // how high cam Y is (larger = higher up / more face)
    camZ:       2.35,  // zoom (smaller = closer)
    camX:      -0.2,   // horizontal shift
  }
  ;(function loadFaceCam() {
    try {
      const s = localStorage.getItem(FACE_CAM_KEY)
      if (s) Object.assign(faceCamParams, JSON.parse(s))
    } catch(e) {}
  })()

  const faceCamGui = new GUI({ title: 'Face Cam ⠿ drag to move', width: 260 })
  faceCamGui.hide()
  const fcEl = faceCamGui.domElement
  fcEl.style.position = 'fixed'
  fcEl.style.top = '12px'
  fcEl.style.left = '12px'
  fcEl.style.zIndex = '9999'
  document.body.appendChild(fcEl)

  // Make draggable
  ;(function makeFcDraggable() {
    const header = fcEl.querySelector('.title')
    if (!header) return
    let ox=0, oy=0, mx=0, my=0, dragging=false
    header.style.cursor='grab'
    header.addEventListener('mousedown', e => {
      dragging=true; mx=e.clientX-fcEl.offsetLeft; my=e.clientY-fcEl.offsetTop
      header.style.cursor='grabbing'; e.preventDefault()
    })
    document.addEventListener('mousemove', e => { if(dragging){ fcEl.style.left=(e.clientX-mx)+'px'; fcEl.style.top=(e.clientY-my)+'px' } })
    document.addEventListener('mouseup', () => { dragging=false; header.style.cursor='grab' })
  })()

  faceCamGui.add(faceCamParams, 'headOffset', 0, 4, 0.05).name('Cam Y (head offset)')
  faceCamGui.add(faceCamParams, 'camZ',       1.0, 6.0, 0.05).name('Cam Z (zoom)')
  faceCamGui.add(faceCamParams, 'camX',       -3, 3, 0.05).name('Cam X (pan)')
  faceCamGui.add({ log() {
    console.log('=== FACE CAM VALUES ===\n' + JSON.stringify(faceCamParams, null, 2))
    localStorage.setItem(FACE_CAM_KEY, JSON.stringify(faceCamParams))
  }}, 'log').name('📋 Save & Log to Console')
  faceCamGui.add({ reset() {
    localStorage.removeItem(FACE_CAM_KEY)
    location.reload()
  }}, 'reset').name('🗑️ Reset to Defaults')

  // ── Mobile Camera GUI ─────────────────────────────────────────────────────
  const MOB_CAM_KEY = 'mob-cam-settings-v38'
  const mobCam = {
    // Hero (original framing)
    heroCamY:       -0.2,
    heroCamZ:        5.85,
    heroCamX:        0,
    // Hero skin picker position (mobile)
    heroPickerBottom: 27,
    heroPickerLeft:   50,
    // About / Studio (section 1 — scroll 750)
    aboutCamY:       -2.2,
    aboutCamZ:        7.9,
    aboutCamX:        0.1,
    aboutTextBottom:   4.5,  // % from bottom
    aboutPickerTop:   29.5,  // % from top (mobile — vertical, left side)
    aboutPickerLeft:   11,   // % from left (mobile)
    lgMobX:             0,   // mobile model X offset
    lgMobY:             0,   // mobile model Y offset
    // Characters — face closeup
    faceHeadOffset:  2.0,
    faceCamZ:        3.15,
    faceCamX:       -0.2,
    // Characters — spread view (shared Z / Y)
    spreadCamZ:      5.4,
    spreadEndY:      1.2,
    spreadMaxX:      2.0,
    spreadCamX:      0.1,
    // Per-character fine-tune offsets (mobile only)
    char1X:  0.0,  char1Y:  0.0,  char1Z: 5.5,   // Duo
    char2X:  0.3,  char2Y: -0.5,  char2Z: 9.6,   // Guardians
    char3X:  0.2,  char3Y: -0.2,  char3Z: 4.3,   // Axolotls
    // Environment
    envCamY:        -1.9,
    envCamZ:         15.1,
    envCamX:         2.1,
    envLookX:       -1.5,
    envLookY:        1.1,
    // Animation
    animCamYOffset:  1.65,
    animCamZ:        10.6,
    animCamX:        0.65,
    // Contact
    contactCamZ:     7.5,
    contactDescentY: 3,
    contactCamX:     0,
  }
  ;(function loadMobCam() {
    try { const s = localStorage.getItem(MOB_CAM_KEY); if (s) Object.assign(mobCam, JSON.parse(s)) } catch(e) {}
    // Apply text + picker positions immediately on mobile
    if (isMobile) {
      const _ap = document.getElementById('lg-color-picker')
      if (_ap) {
        _ap.style.top       = `${mobCam.aboutPickerTop}%`
        _ap.style.left      = `${mobCam.aboutPickerLeft}%`
        _ap.style.bottom    = 'auto'
        _ap.style.right     = 'auto'
        _ap.style.transform = 'translateY(-50%)'
      }
    }
  })()

  const mobGui = new GUI({ title: 'Mobile Camera ⠿ drag', width: 280 })
  window.__mobGui = mobGui
  mobGui.hide()
  const mobGuiEl = mobGui.domElement
  mobGuiEl.style.position = 'fixed'
  mobGuiEl.style.top  = '12px'
  mobGuiEl.style.left = '12px'
  mobGuiEl.style.zIndex = '9999'
  document.body.appendChild(mobGuiEl)
  ;(function makeMobGuiDraggable() {
    const hdr = mobGuiEl.querySelector('.title'); if (!hdr) return
    let mx=0, my=0, dragging=false
    hdr.style.cursor='grab'
    hdr.addEventListener('mousedown', e => { dragging=true; mx=e.clientX-mobGuiEl.offsetLeft; my=e.clientY-mobGuiEl.offsetTop; hdr.style.cursor='grabbing'; e.preventDefault() })
    document.addEventListener('mousemove', e => { if(dragging){ mobGuiEl.style.left=(e.clientX-mx)+'px'; mobGuiEl.style.top=(e.clientY-my)+'px' } })
    document.addEventListener('mouseup', () => { dragging=false; hdr.style.cursor='grab' })
  })()

  const mobHeroFolder = mobGui.addFolder('Hero')
  mobHeroFolder.add(mobCam, 'heroCamY', -3, 3,  0.05).name('Y')
  mobHeroFolder.add(mobCam, 'heroCamZ',  2, 10, 0.05).name('Z (zoom)')
  mobHeroFolder.add(mobCam, 'heroCamX', -4, 4,  0.05).name('X')
  mobHeroFolder.add(mobCam, 'heroPickerBottom', 0, 60, 1).name('Picker Bottom %')
  mobHeroFolder.add(mobCam, 'heroPickerLeft',   0, 100, 1).name('Picker Left %')
  mobHeroFolder.close()

  const aboutPickerEl = document.getElementById('lg-color-picker')

  const mobAboutFolder = mobGui.addFolder('About / Studio')
  mobAboutFolder.add(mobCam, 'aboutCamY', -8, 8,   0.05).name('Model Y')
  mobAboutFolder.add(mobCam, 'aboutCamZ',  2, 20,  0.05).name('Model Z (zoom)')
  mobAboutFolder.add(mobCam, 'aboutCamX', -8, 8,   0.05).name('Model X')
  mobAboutFolder.add(mobCam, 'aboutTextBottom', -20, 100, 0.5).name('Text Bottom %').onChange(v => {
    if (aboutSection) { aboutSection.style.top = 'auto'; aboutSection.style.bottom = `${v}%` }
  })
  mobAboutFolder.add(mobCam, 'aboutPickerTop',  0, 100, 0.5).name('Picker Top %').onChange(v => {
    if (aboutPickerEl) { aboutPickerEl.style.top = `${v}%`; aboutPickerEl.style.bottom = 'auto' }
  })
  mobAboutFolder.add(mobCam, 'aboutPickerLeft', 0, 50,  0.5).name('Picker Left %').onChange(v => {
    if (aboutPickerEl) { aboutPickerEl.style.left = `${v}%`; aboutPickerEl.style.right = 'auto' }
  })
  mobAboutFolder.add(mobCam, 'lgMobX', -3, 3, 0.05).name('Model X')
  mobAboutFolder.add(mobCam, 'lgMobY', -3, 3, 0.05).name('Model Y')
  mobAboutFolder.close()

  const mobFaceFolder = mobGui.addFolder('Characters — Face')
  mobFaceFolder.add(mobCam, 'faceHeadOffset', 0, 5, 0.05).name('Y (head offset)')
  mobFaceFolder.add(mobCam, 'faceCamZ',       1, 8, 0.05).name('Z (zoom)')
  mobFaceFolder.add(mobCam, 'faceCamX',      -4, 4, 0.05).name('X')
  mobFaceFolder.close()

  const mobSpreadFolder = mobGui.addFolder('Characters — Spread')
  mobSpreadFolder.add(mobCam, 'spreadEndY',  0, 4,  0.05).name('Y end offset')
  mobSpreadFolder.add(mobCam, 'spreadCamZ',  2, 12, 0.05).name('Z (zoom)')
  mobSpreadFolder.add(mobCam, 'spreadCamX', -4, 4,  0.05).name('X offset')
  const mobChar1Folder = mobSpreadFolder.addFolder('Duo')
  mobChar1Folder.add(mobCam, 'char1X', -3, 3,  0.05).name('X')
  mobChar1Folder.add(mobCam, 'char1Y', -3, 3,  0.05).name('Y')
  mobChar1Folder.add(mobCam, 'char1Z',  2, 14, 0.1).name('Z (zoom)')
  mobChar1Folder.close()
  const mobChar2Folder = mobSpreadFolder.addFolder('Guardians')
  mobChar2Folder.add(mobCam, 'char2X', -3, 3,  0.05).name('X')
  mobChar2Folder.add(mobCam, 'char2Y', -3, 3,  0.05).name('Y')
  mobChar2Folder.add(mobCam, 'char2Z',  2, 14, 0.1).name('Z (zoom)')
  mobChar2Folder.close()
  const mobChar3Folder = mobSpreadFolder.addFolder('Axolotls')
  mobChar3Folder.add(mobCam, 'char3X', -3, 3,  0.05).name('X')
  mobChar3Folder.add(mobCam, 'char3Y', -3, 3,  0.05).name('Y')
  mobChar3Folder.add(mobCam, 'char3Z',  2, 14, 0.1).name('Z (zoom)')
  mobChar3Folder.close()
  mobSpreadFolder.close()

  const mobEnvFolder = mobGui.addFolder('Environment')
  mobEnvFolder.add(mobCam, 'envCamY',   -5, 10, 0.1).name('Cam Y')
  mobEnvFolder.add(mobCam, 'envCamZ',    5, 30, 0.1).name('Cam Z (zoom)')
  mobEnvFolder.add(mobCam, 'envCamX',   -6,  6, 0.1).name('Cam X')
  mobEnvFolder.add(mobCam, 'envLookX', -10, 10, 0.1).name('Look X (model L/R)')
  mobEnvFolder.add(mobCam, 'envLookY', -10,  5, 0.1).name('Look Y (model up/down)')
  mobEnvFolder.close()

  const mobAnimFolder = mobGui.addFolder('Animation')
  mobAnimFolder.add(mobCam, 'animCamYOffset', -2, 5,  0.05).name('Y offset')
  mobAnimFolder.add(mobCam, 'animCamZ',        4, 20, 0.1 ).name('Z (zoom)')
  mobAnimFolder.add(mobCam, 'animCamX',       -6, 6,  0.05).name('X')
  mobAnimFolder.close()

  const mobContactFolder = mobGui.addFolder('Contact')
  mobContactFolder.add(mobCam, 'contactDescentY', 0, 8,  0.05).name('Y descent')
  mobContactFolder.add(mobCam, 'contactCamZ',     3, 14, 0.05).name('Z (zoom)')
  mobContactFolder.add(mobCam, 'contactCamX',    -6, 6,  0.05).name('X')
  mobContactFolder.close()

  mobGui.add({ save() {
    localStorage.setItem(MOB_CAM_KEY, JSON.stringify(mobCam))
    const json = JSON.stringify(mobCam, null, 2)
    console.log('=== MOBILE CAM VALUES ===\n' + json)
    // Show overlay so values are easy to copy on mobile
    let ov = document.getElementById('_mobCamOverlay')
    if (!ov) {
      ov = document.createElement('div')
      ov.id = '_mobCamOverlay'
      Object.assign(ov.style, {
        position:'fixed', top:'0', left:'0', width:'100%', height:'100%',
        background:'rgba(0,0,0,0.85)', zIndex:'99999', display:'flex',
        flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'20px', boxSizing:'border-box'
      })
      document.body.appendChild(ov)
    }
    ov.innerHTML = `
      <div style="color:#a78bfa;font-family:monospace;font-size:13px;margin-bottom:12px;font-weight:bold;">MOBILE CAM VALUES — share with Claude</div>
      <textarea id="_mobCamText" readonly style="width:100%;max-width:480px;height:320px;background:#1a0a2e;color:#e2e8f0;font-family:monospace;font-size:12px;border:1px solid #6d28d9;border-radius:6px;padding:10px;resize:none;">${json}</textarea>
      <button id="_mobCamCopy" style="margin-top:12px;padding:10px 28px;background:#7c3aed;color:#fff;border:none;border-radius:6px;font-size:14px;cursor:pointer;">Copy</button>
      <button id="_mobCamClose" style="margin-top:8px;padding:8px 24px;background:transparent;color:#a78bfa;border:1px solid #6d28d9;border-radius:6px;font-size:13px;cursor:pointer;">Close</button>`
    document.getElementById('_mobCamCopy').onclick = () => {
      document.getElementById('_mobCamText').select()
      document.execCommand('copy')
      document.getElementById('_mobCamCopy').textContent = 'Copied ✓'
    }
    document.getElementById('_mobCamClose').onclick = () => ov.remove()
  }}, 'save').name('💾 Save Values')
  mobGui.add({ reset() { localStorage.removeItem(MOB_CAM_KEY); location.reload() }}, 'reset').name('🗑️ Reset')

  // Pre-compile all shaders to avoid GPU stall on first visibility toggle.
  // model6, model3, model4 start hidden (visible=false) — Three.js skips them
  // during compile unless we temporarily show them. This runs once before the
  // animation loop and prevents the lag spike when they first become visible.
  model6.visible = true
  model3.visible = true
  model4.visible = true
  animModel.visible = true
  envModel.visible = true
  lgYellow.visible   = true   // pre-compile LG variants
  lgGreen.visible    = true
  heroYellow.visible = true   // pre-compile hero skin variants
  heroRed.visible    = true
  renderer.compile(scene, camera)

  // Pre-compile the transparent variant of env materials.
  // Without this, the first Characters→Environments fade-in switches env mats from
  // opaque→transparent for the first time, causing a shader recompile mid-transition.
  envMats.forEach(m => { m.transparent = true; m.needsUpdate = true })
  renderer.compile(scene, camera)
  envMats.forEach(m => { m.transparent = m._envHasAlpha; m.needsUpdate = true })
  // Force GPU buffer uploads. Models sit at y=-6/-9/-12 — outside the startup
  // camera frustum — so Three.js would cull them and skip the upload. Disable
  // frustum culling temporarily so render() actually processes every object.
  ;[model6, model3, model4, animModel, envModel].forEach(m => m.traverse(c => { c.frustumCulled = false }))
  renderer.render(scene, camera)
  ;[model6, model3, model4, animModel, envModel].forEach(m => m.traverse(c => { c.frustumCulled = true }))
  // Keep envModel frustum culling disabled permanently — during its fade-in the camera is
  // still sweeping from Characters position, so individual mesh bounding boxes enter the
  // frustum at different times and cause a staggered "pop-in" effect. The terrain is always
  // rendered as a whole unit regardless of camera angle, which is the correct behaviour.
  envModel.traverse(c => { c.frustumCulled = false })

  model6.visible = false
  model3.visible = false
  model4.visible = false
  animModel.visible = false
  envModel.visible = false
  lgYellow.visible   = false  // reset LG to default (purple is default-on)
  lgGreen.visible    = false
  heroYellow.visible = false  // reset hero variants (purple is default-on)
  heroRed.visible    = false

  function animate() {
    requestAnimationFrame(animate)
    try {

    const elapsed = clock.getElapsedTime()
    const scroll  = updateScroll()

    // Progress 0→1 for each phase
    // p2t starts at DESCENT_START so camera movement begins before rotation ends
    const p2t  = Math.min(Math.max((scroll - DESCENT_START) / (P2_END  - DESCENT_START), 0), 1)
    const p3t  = Math.min(Math.max((scroll - P2_END)  / (P3_END  - P2_END),  0), 1)
    const p4t  = Math.min(Math.max((scroll - P3_END)  / (P4_END  - P3_END),  0), 1)
    const p5t  = Math.min(Math.max((scroll - P4D_END) / (P5_END  - P4D_END), 0), 1)
    const collectT  = Math.min(Math.max((scroll - P_ANIM_END) / (P_COLLECT_END - P_ANIM_END), 0), 1)
    const collectE  = easeInOut(collectT)
    const contactT  = Math.min(Math.max((scroll - P_COLLECT_END) / (P_CONTACT_END - P_COLLECT_END), 0), 1)
    const cEase     = easeInOut(contactT)

    // ── Model 1: rotation locks at P1_END, fades out into About section ──────
    // Only auto-rotate while model 1 is in frame (camera hasn't gone to model 2)
    if (p2t < 1) autoAngle += 0.0025
    const scrollRot1 = Math.min(Math.max(scroll, 0), P1_END)
    model1.rotation.y = autoAngle - scrollRot1 * 0.007 + userRot.m1
    model1.position.y = model1BaseY + Math.sin(elapsed * 0.7) * 0.06 * (1 - p2t)
    // Fade model1 out before the About snap at scroll=750 so screen is clean
    // Fade starts at 400, fully gone by 730 (just before the snap point)
    const model1Opacity = Math.max(0, Math.min(1, 1 - (scroll - 400) / 330))
    // Show the active hero skin; all others hidden
    model1.visible     = model1Opacity > 0.01 && activeHeroColor === 'purple'
    heroYellow.visible = model1Opacity > 0.01 && activeHeroColor === 'yellow'
    heroRed.visible    = model1Opacity > 0.01 && activeHeroColor === 'red'

    // Apply fade opacity to all skins
    model1Mats.forEach(m => { m.opacity = model1Opacity; m.depthWrite = model1Opacity > 0.01 })
    heroYellowMats.forEach(m => { m.opacity = model1Opacity; m.depthWrite = model1Opacity > 0.01 })
    heroRedMats.forEach(m =>    { m.opacity = model1Opacity; m.depthWrite = model1Opacity > 0.01 })

    // Sync variant transforms to model1 (position + rotation computed above)
    heroYellow.position.copy(model1.position); heroYellow.rotation.copy(model1.rotation)
    heroRed.position.copy(model1.position);    heroRed.rotation.copy(model1.rotation)

    const heroAnyVisible = model1Opacity > 0.01
    if (heroAnyVisible && !model1WasVisible) { userRot.m1 = 0; rotVelocity.m1 = 0 }
    model1WasVisible = heroAnyVisible

    // Balance bloom across hero skins — yellow/red are more luminant than purple,
    // so raise the threshold when they're active to avoid over-blooming.
    const heroBloomBase = 2.5   // must exceed the HDR sky base (~2.0) — this overrides postfx every frame
    bloomPass.threshold = heroAnyVisible && activeHeroColor !== 'purple'
      ? heroBloomBase + 0.06
      : heroBloomBase

    // Hero color picker — fade with model1, always visible while hero is on screen
    heroColorPicker.style.opacity       = String(model1Opacity)
    heroColorPicker.style.pointerEvents = heroAnyVisible ? 'all' : 'none'
    if (isMobile) {
      heroColorPicker.style.bottom    = `${mobCam.heroPickerBottom}%`
      heroColorPicker.style.left      = `${mobCam.heroPickerLeft}%`
      heroColorPicker.style.right     = 'auto'
      heroColorPicker.style.top       = 'auto'
      heroColorPicker.style.transform = mobCam.heroPickerLeft === 50 ? 'translateX(-50%)' : 'none'
    } else {
      heroColorPicker.style.right     = `${charParams.heroPickerRight}%`
      heroColorPicker.style.top       = `${charParams.heroPickerTop}%`
      heroColorPicker.style.left      = 'auto'
      heroColorPicker.style.bottom    = 'auto'
      heroColorPicker.style.transform = 'translateY(-50%)'
    }

    // ── Camera path ───────────────────────────────────────────────────────────
    const faceY = -(sectionGap - faceCamParams.headOffset)

    // Mobile: head sits at y ≈ -(sectionGap - 2.0) = -4.0
    const mobileRevealY = -(sectionGap - mobCam.faceHeadOffset)

    // Single progress variable for the entire snap2 → snap3 sweep.
    const charSpreadT = Math.min(Math.max((scroll - P2_END) / (P5_END - P2_END), 0), 1)
    const charSpreadE = easeInOut(charSpreadT)

    // Progress for Characters spread → Environments top-down
    const p_env_t = Math.min(Math.max((scroll - P5_END) / (P_ENV_END - P5_END), 0), 1)
    const p_env_e = easeInOut(p_env_t)

    // Progress for Environments → Animation snap
    const p_anim_t = Math.min(Math.max((scroll - P_ENV_END) / (P_ANIM_END - P_ENV_END), 0), 1)
    const p_anim_e = easeInOut(p_anim_t)

    // Camera for isometric env section — values driven by GUI when available
    const _ecp = window.__envCamParams
    const ENV_CAM_Y = isMobile ? mobCam.envCamY : (_ecp ? _ecp.camY : 0.7)
    const ENV_CAM_Z = isMobile ? mobCam.envCamZ : (_ecp ? _ecp.camZ : 14.5)

    // Camera Y target — desktop looks slightly lower so model sits higher in frame
    const _animCam = window.__animGuiParams ? window.__animGuiParams.cam : null
    const ANIM_CAM_Y = ANIM_WORLD_Y + (isMobile ? mobCam.animCamYOffset : (_animCam ? _animCam.camYOffset : 1.1))

    // Mobile: step camera X through each model individually (one model per snap)
    const mobCarT1   = isMobile ? Math.min(Math.max((scroll - P2_END)  / (MOB_CAR1 - P2_END), 0), 1) : 0
    const mobCarT2   = isMobile ? Math.min(Math.max((scroll - MOB_CAR1) / (P5_END  - MOB_CAR1), 0), 1) : 0
    const mobCharXOff = isMobile
      ? lerp(lerp(mobCam.char1X, mobCam.char2X, easeInOut(mobCarT1)), mobCam.char3X, easeInOut(mobCarT2))
      : 0
    const mobCharYOff = isMobile
      ? lerp(lerp(mobCam.char1Y, mobCam.char2Y, easeInOut(mobCarT1)), mobCam.char3Y, easeInOut(mobCarT2))
      : 0
    const mobCharZ = isMobile
      ? lerp(lerp(mobCam.char1Z, mobCam.char2Z, easeInOut(mobCarT1)), mobCam.char3Z, easeInOut(mobCarT2))
      : mobCam.spreadCamZ

    let camY, camZ
    if (p_anim_t > 0) {
      // Anim starts from env camera position for a smooth descent
      camY = lerp(ENV_CAM_Y, ANIM_CAM_Y, p_anim_e)
      // Mobile: pull back more so the model reads smaller and centred
      camZ = lerp(ENV_CAM_Z, isMobile ? mobCam.animCamZ : (_animCam ? _animCam.camZ : 7.8), p_anim_e)
    } else if (p_env_t > 0) {
      // Rise above characters → isometric overview of terrain
      camY = lerp(-sectionGap + (isMobile ? mobCam.spreadEndY : 1.2), ENV_CAM_Y, p_env_e)
      camZ = lerp(isMobile ? mobCam.spreadCamZ : 4.8, ENV_CAM_Z, p_env_e)
    } else if (charSpreadT > 0) {
      // One smooth sweep: face closeup → all-characters spread
      camY = lerp(isMobile ? mobileRevealY : faceY, -sectionGap + (isMobile ? mobCam.spreadEndY : 1.2), charSpreadE) + (isMobile ? mobCharYOff : 0)
      camZ = isMobile ? lerp(mobCam.faceCamZ, mobCharZ, charSpreadE) : lerp(faceCamParams.camZ, 4.8, charSpreadE)
    } else {
      // Mobile: hero (scroll 0) → about (scroll 750) → face closeup (scroll 1700)
      // Desktop: straight lerp hero → face closeup
      if (isMobile) {
        const aboutT = easeInOut(Math.min(scroll / 750, 1))
        const midY = lerp(mobCam.heroCamY, mobCam.aboutCamY, aboutT)
        const midZ = lerp(mobCam.heroCamZ, mobCam.aboutCamZ, aboutT)
        camY = lerp(midY, mobileRevealY, easeInOut(p2t))
        camZ = lerp(midZ, mobCam.faceCamZ, easeInOut(p2t))
      } else {
        camY = lerp(0, faceY, easeInOut(p2t))
        camZ = lerp(5.0, faceCamParams.camZ, easeInOut(p2t))
      }
    }

    // Camera X: sweeps to MAX_CAR_X during spread, centres during env + anim sections
    inCarouselSection = charSpreadT > 0 && p5t < 1
    // Hero X lerps into face X; on mobile also includes heroCamX start
    const aboutXMid   = isMobile ? lerp(mobCam.heroCamX, mobCam.aboutCamX, easeInOut(Math.min(scroll / 750, 1))) : 0
    const heroXStart  = isMobile ? aboutXMid : 0
    const faceXOffset = (isMobile ? mobCam.faceCamX : faceCamParams.camX) * (1 - charSpreadE) * (charSpreadT === 0 ? easeInOut(p2t) : 1)
    const heroXOffset = isMobile ? lerp(heroXStart, 0, easeInOut(p2t)) : 0
    const mobCarX    = isMobile
      ? lerp(0, 2, easeInOut(mobCarT1)) + lerp(0, 2, easeInOut(mobCarT2)) + mobCharXOff
      : 0
    // Per-section X offsets — blend in/out with their section's progress
    const mobSpreadXOffset  = isMobile ? mobCam.spreadCamX  * charSpreadE  * (1 - p_env_e)  * (1 - p_anim_e) : 0
    const mobEnvXOffset     = isMobile ? mobCam.envCamX     * p_env_e      * (1 - p_anim_e) : 0
    const mobAnimXOffset    = isMobile ? mobCam.animCamX    * p_anim_e     * (1 - collectE) : 0
    const camXBase = (isMobile ? mobCarX : charSpreadE * MAX_CAR_X) * (1 - p_env_e) * (1 - p_anim_e) + faceXOffset + heroXOffset + mobSpreadXOffset + mobEnvXOffset + mobAnimXOffset

    // Collectibles + Contact: centre camera X, descend gently, pull back
    const camX = lerp(camXBase, 0, Math.max(collectE, cEase))

    const collectY  = camY - collectE * 2.2   // small descent into collectibles
    const finalCamY = lerp(collectY, collectY - (isMobile ? mobCam.contactDescentY : 3), cEase)
    const finalZ    = lerp(camZ, isMobile ? mobCam.contactCamZ : 7.5, cEase)

    // Smooth mouse parallax — lazily chase raw mouse position
    const MOUSE_EASE = 0.04
    mouseLerp.x += (mouse.x - mouseLerp.x) * MOUSE_EASE
    mouseLerp.y += (mouse.y - mouseLerp.y) * MOUSE_EASE

    // Parallax strength — consistent across all sections
    const parStrength = 0.18
    const parX = mouseLerp.x * parStrength
    const parY = mouseLerp.y * parStrength * 0.55   // vertical feels more natural at ~half

    // On desktop, offset lookAt left so model appears shifted right (text on left)
    // Stays active through P3 (torso → full-body reveal) then fades as carousel pans.
    // Animation section re-applies the same offset to match the hero layout.
    const desktopX = window.innerWidth > 640
      ? -1.15 * (1 - easeInOut(p2t)) - 0.55 * easeInOut(p2t) * (1 - easeInOut(p4t))
      : 0
    // Animation section: look right → model sits left; mobile: centred (offset = 0)
    const _animXBase = (_animCam ? _animCam.camXOffset : 3.2)
    const animOffsetX = window.innerWidth > 640 ? _animXBase * p_anim_e * (1 - collectE) : 0
    // Environments blend factor — computed early so we can use it in camera.position
    const envBlend = p_env_e * (1 - p_anim_e)  // 1 when fully in env, 0 otherwise

    // Env section: physically slide camera with cursor (dolly parallax).
    const ENV_CAM_DOLLY_X = mouseLerp.x * 1.2 * envBlend
    const ENV_CAM_DOLLY_Y = mouseLerp.y * 1.0 * envBlend

    const contactBlend = cEase * (1 - envBlend)
    const CONTACT_DOLLY_X = mouseLerp.x * 0.6 * contactBlend
    const CONTACT_DOLLY_Y = mouseLerp.y * 0.3 * contactBlend

    const mobContactXOff = isMobile ? mobCam.contactCamX * cEase : 0
    camera.position.set(camX + ENV_CAM_DOLLY_X + CONTACT_DOLLY_X + mobContactXOff, finalCamY + ENV_CAM_DOLLY_Y + CONTACT_DOLLY_Y, finalZ)

    // Environments: shift lookAt left so the model sits in the right half of screen.
    // Text panel occupies left 36% on desktop — env model fills the right portion.
    // Shift lookAt left → model drifts right in frame; lookAt Y above terrain → model shifts up
    const ENV_LOOK_X  = isMobile ? mobCam.envLookX : (_ecp ? _ecp.lookX : -4.8)
    const ENV_LOOK_Y  = ENV_WORLD_Y + (isMobile ? mobCam.envLookY : (_ecp ? _ecp.lookYOffset : -3.0))
    const normalLookX = desktopX + camX + parX + animOffsetX
    const normalLookY = finalCamY + parY
    // lookAt slides with each dolly so the view direction stays locked on the scene —
    // the parallax effect comes from the camera position change, not camera rotation.
    const lookAtX = lerp(normalLookX + CONTACT_DOLLY_X, ENV_LOOK_X + ENV_CAM_DOLLY_X, envBlend)
    const lookAtY = lerp(normalLookY + CONTACT_DOLLY_Y, ENV_LOOK_Y + ENV_CAM_DOLLY_Y, envBlend)
    camera.lookAt(lookAtX, lookAtY, 0)

    // ── Model 2: scroll-driven rotation + gentle float + closeup tilt ─────────
    // Reset autoAngle2 when camera leaves the characters section (scrolling back up)
    if (prevP3t > 0 && charSpreadT === 0) autoAngle2 = 0.0
    prevP3t = charSpreadT
    // Only auto-rotate while model 2 is in frame
    // ── Carousel models: gentle sin sway, always front-facing (never shows back)
    // All carousel models: same slow frequency, low amplitude — consistent feel
    const ROT_FREQ = 0.22
    const ROT_AMP  = 0.40

    // Subtle idle sway — small amplitude, slow, each model out of phase
    const SWAY_AMP   = 0.14   // ~8 degrees
    const SWAY_SPEED = 0.45   // radians/sec
    const sway3 = Math.sin(elapsed * SWAY_SPEED + 0.0)  * SWAY_AMP
    const sway4 = Math.sin(elapsed * SWAY_SPEED + 2.1)  * SWAY_AMP
    const sway6 = Math.sin(elapsed * SWAY_SPEED + 4.2)  * SWAY_AMP

    // 2lgs: mirror left group, facing inward (toward center)
    model3.rotation.y = charParams.axolotlRotY + userRot.m3 + sway3
    model3.position.y = model2BaseY + charParams.axolotlOffsetY

    // Guardians center: slight 3/4 angle
    model4.rotation.y = charParams.guardianRotY + userRot.m4 + sway4
    model4.position.y = model2BaseY + charParams.guardianOffsetY


    model6.rotation.y = charParams.duoRotY + userRot.m6 + sway6
    model6.rotation.x = lerp(0, -0.10, easeInOut(p2t)) * (1 - charSpreadE)
    model6.position.y = model2BaseY + charParams.duoOffsetY

    // Sequential fade: characters must be fully gone before the env/anim models appear.
    // Using raw p_env_t/p_anim_t (not easeInOut) for a fast, snappy exit —
    // characters are fully out by the first 40% of each transition.
    const charFadeToEnv  = 1 - Math.min(Math.max(p_env_t  / 0.40, 0), 1)
    const charFadeToAnim = 1 - Math.min(Math.max(p_anim_t / 0.40, 0), 1)

    // Hero (model6): snap visible — no opacity fade to prevent clothing bleed-through
    const heroVisible = scroll > 950 && charFadeToEnv > 0 && charFadeToAnim > 0
    model6.visible = heroVisible
    if (model6.visible && !model6WasVisible) { userRot.m6 = 0; rotVelocity.m6 = 0 }
    model6WasVisible = model6.visible

    // Companions: snap visible — same reason, no opacity fade
    const companionsVisible = charSpreadT > 0.05 && charFadeToEnv > 0 && charFadeToAnim > 0
    model3.visible = companionsVisible
    model4.visible = companionsVisible
    if (companionsVisible && !companionsWasVisible) {
      userRot.m3 = 0; rotVelocity.m3 = 0
      userRot.m4 = 0; rotVelocity.m4 = 0
    }
    companionsWasVisible = companionsVisible

    prevScroll = scroll

    // Animate nebula sky dome
    updateSky(elapsed)

    // Fade out hero tagline — fully gone before About section snap at scroll 750
    const taglineOpacity = Math.max(0, 1 - scroll / 620)
    heroTagline.style.opacity = taglineOpacity
    heroTagline.style.transform = `translateY(${(1 - taglineOpacity) * 10}px)`
    // Gate visibility so the CTA buttons aren't clickable while invisible
    heroTagline.style.visibility = taglineOpacity > 0.05 ? 'visible' : 'hidden'
    if (heroFeatures) {
      heroFeatures.style.opacity    = taglineOpacity
      heroFeatures.style.visibility = taglineOpacity > 0.05 ? 'visible' : 'hidden'
    }
    if (heroDecor) heroDecor.style.opacity = taglineOpacity

    // About section — fades in early, then fades out quickly after the snap at 750
    // so the camera descent to Characters is clean (no text overlap)
    const aboutIn  = Math.min(Math.max((scroll - 600) / 160, 0), 1)
    const aboutOut = Math.min(Math.max((scroll - 900) / 200, 0), 1)
    const aboutOpacity = aboutIn * (1 - aboutOut)
    aboutSection.style.opacity = aboutOpacity
    // Enable hover on the studio cards only while the section is visible
    aboutSection.style.pointerEvents = aboutOpacity > 0.5 ? 'all' : 'none'

    // ── LG about-section model ─────────────────────────────────────────────────
    const lgVisible = false   // LG model retired with the Studio redesign
    // Reset rotation to start position every time the model comes back into view
    if (lgVisible && !lgWasVisible) {
      lgAngle        = 0
      userRot.lg     = 0
      rotVelocity.lg = 0
    }
    lgWasVisible = lgVisible
    lgAngle += 0.0025  // slow auto-rotate, same pace as hero model
    // Velocity decay for lg (separate from modelMap loop)
    const lgDragging = (modelDragKey === 'lg' && modelDragActive) ||
                       (modelTouchKey === 'lg' && modelTouchLastX !== null)
    if (!lgDragging) {
      if (Math.abs(rotVelocity.lg) > 0.0001) {
        userRot.lg      += rotVelocity.lg
        rotVelocity.lg  *= 0.90
      } else {
        rotVelocity.lg = 0
      }
    }
    const lgFinalRot  = lgParams.rotationY + lgAngle + userRot.lg
    const lgFloatY    = lgParams.modelY + Math.sin(elapsed * 0.7) * 0.06
    lgModels.forEach(m => {
      m.rotation.y = lgFinalRot
      m.position.x = lgParams.modelX + (isMobile ? mobCam.lgMobX : 0)
      m.position.y = lgFloatY        + (isMobile ? mobCam.lgMobY : 0)
      m.position.z = lgParams.modelZ
      if (isMobile) m.scale.setScalar(lgParams.scale)
    })
    // Visibility — simple on/off, no opacity fading on the model itself
    lgPurple.visible = activeLGColor === 'purple' && lgVisible
    lgYellow.visible = activeLGColor === 'yellow' && lgVisible
    lgGreen.visible  = activeLGColor === 'green'  && lgVisible
    // Sync color picker — mobile: vertical right side; desktop: GUI-driven
    lgColorPicker.style.opacity      = String(aboutOpacity)
    lgColorPicker.style.pointerEvents = lgVisible ? 'all' : 'none'
    if (isMobile) {
      lgColorPicker.style.top       = `${mobCam.aboutPickerTop}%`
      lgColorPicker.style.left      = `${mobCam.aboutPickerLeft}%`
      lgColorPicker.style.bottom    = 'auto'
      lgColorPicker.style.right     = 'auto'
      lgColorPicker.style.transform = 'translateY(-50%)'
    } else {
      lgColorPicker.style.left      = `${lgParams.pickerLeft}%`
      lgColorPicker.style.bottom    = `${lgParams.pickerBottom}%`
      lgColorPicker.style.right     = 'auto'
      lgColorPicker.style.top       = 'auto'
      lgColorPicker.style.transform = lgParams.pickerLeft === 50 ? 'translateX(-50%)' : 'none'
    }
    // Rotate hint — shown only during about section, positioned near the model
    rotateHint.style.opacity  = String(aboutOpacity * 0.85)
    rotateHint.style.left     = `${lgParams.hintLeft}%`
    rotateHint.style.bottom   = `${lgParams.hintBottom}%`
    rotateHint.style.transform = lgParams.hintLeft === 50 ? 'translateX(-50%)' : 'none'

    // Characters label — fades in before P2_END so text is fully visible at the snap
    // at scroll 1100. Fades out into contact section.
    // Mobile: also fades out mid-P3 as camera pulls back for full body reveal.
    const charIn       = Math.min(Math.max((scroll - 980) / 100, 0), 1)
    // Fade text out as soon as the carousel row starts scrolling
    const charOut      = Math.min(Math.max((scroll - (P3_END + 200)) / 400, 0), 1)
    const charMobileOut = isMobile ? Math.min(Math.max((p3t - 0.55) / 0.2, 0), 1) : 0
    const charOpacity = charIn * (1 - charMobileOut) * (1 - charOut)
    charactersSection.style.opacity = charOpacity
    if (charFeatures) charFeatures.style.opacity = charOpacity

    // Gallery hint pill — appears once the spread view has fully arrived,
    // gone again as the camera lifts into the Environments section
    const hintIn  = Math.min(Math.max((charSpreadT - 0.9) / 0.1, 0), 1)
    const hintOut = Math.min(Math.max(p_env_t / 0.25, 0), 1)
    const galleryHintOpacity = hintIn * (1 - hintOut)
    if (galleryHint) {
      galleryHint.style.opacity = galleryHintOpacity
      galleryHint.style.pointerEvents = galleryHintOpacity > 0.5 ? 'all' : 'none'
    }

    // Contact section — fades in after collectibles, fully interactive when visible
    const contactIn = Math.min(Math.max((contactT - 0.25) / 0.5, 0), 1)
    if (contactSection) {
      contactSection.style.opacity = contactIn
      contactSection.style.pointerEvents = contactIn > 0.05 ? 'all' : 'none'
    }

    // Digital Collectibles — fades in after animation is out, fades out into contact
    const collectIn      = Math.min(Math.max((collectT - 0.45) / 0.40, 0), 1)
    const collectOut     = easeInOut(Math.min(contactT / 0.35, 1))
    const collectOpacity = collectIn * (1 - collectOut)
    if (collectiblesSection) {
      collectiblesSection.style.opacity = collectOpacity
      collectiblesSection.style.pointerEvents = collectOpacity > 0.05 ? 'all' : 'none'
      // Front-card videos run while the section is on screen; the cards hidden
      // behind them only decode while their deck is fanned open (hover or tap)
      const sectionVisible = collectOpacity > 0.05
      collectStacks.forEach(s => {
        const fanned = sectionVisible && (s.el.classList.contains('fanned') || s.el.matches(':hover'))
        setVideoPlaying(s.frontVideo, sectionVisible)
        s.backVideos.forEach(v => setVideoPlaying(v, fanned))
      })
    }

    // Environments section — fades in after characters are out, fades out before anim model appears
    const envIn  = Math.min(Math.max((p_env_t - 0.45) / 0.40, 0), 1)
    const envOut = Math.min(Math.max(p_anim_t / 0.40, 0), 1)   // fast exit, matches model
    const envOpacity = envIn * (1 - envOut)
    if (environmentsSection) environmentsSection.style.opacity = envOpacity
    if (envGalleryHint) {
      envGalleryHint.style.opacity = envOpacity
      envGalleryHint.style.pointerEvents = envOpacity > 0.5 ? 'all' : 'none'
    }

    // Env terrain — rotate to isometric diamond angle, user-draggable
    const _emp = window.__envModelParams
    const swayEnv = Math.sin(elapsed * 0.45 + 1.0) * 0.14
    envModel.rotation.y = (_emp ? _emp.rotY : 2.616946) + swayEnv

    // Fade in during env section (starts after characters are fully out),
    // fade out before anim model appears — sequential, no overlap.
    // Defined here (above lighting) so lights can sync to model opacity.
    const envFadeIn  = Math.min(Math.max((p_env_t  - 0.45) / 0.40, 0), 1)
    const envFadeOut = Math.min(Math.max( p_anim_t  / 0.40,         0), 1)
    const envModelOpacity = envFadeIn * (1 - envFadeOut)

    // Tie shadow light and global light muting to envModelOpacity (not envBlend).
    // envBlend ramps with camera movement starting before the model appears — using it
    // caused the shadow light to be mid-ramp when leaves first appeared, then continue
    // strengthening while visible, producing a sudden dark-areas shift on the foliage.
    // Syncing to envModelOpacity means lighting is always correct for the current opacity.
    const _esp = window.__envShadowParams
    if (envShadowLight) envShadowLight.intensity = (_esp ? _esp.intensity : 5.1) * envModelOpacity

    const muteAmount = window.__envMuteGlobals !== undefined ? window.__envMuteGlobals : 1.0
    globalLightsToMute.forEach(({ light, base }) => {
      light.intensity = base * (1 - envModelOpacity * muteAmount)
    })
    const envFading = envModelOpacity < 0.999 && envModelOpacity > 0.001
    envMats.forEach(m => {
      if (m._isLeaf) return   // leaves stay opaque always — alphaTest handles cutout
      m.transparent = envFading || m._envHasAlpha
      // Keep depthWrite ON during fading — the terrain is a solid object and should occlude
      // correctly even when semi-transparent. Turning it off causes the transparent render
      // queue to sort meshes by camera distance, making some parts appear before others.
      m.depthWrite  = !m._envHasAlpha
      m.opacity     = envModelOpacity
    })
    envModel.visible = envModelOpacity > 0.01
    if (envModel.visible && !envWasVisible) { userRot.me = 0; rotVelocity.me = 0 }
    envWasVisible = envModel.visible

    // Animation section — fades in as camera arrives, fades out into contact
    const animIn  = Math.min(Math.max((p_anim_t - 0.5) / 0.4, 0), 1)
    const animOut = easeInOut(Math.min(collectT / 0.35, 1))
    animationSection.style.opacity = animIn * (1 - animOut)

    // Animation model — gentle float + facing right, user-draggable
    const _ag = window.__animGuiParams
    const _am = _ag ? _ag.model : null
    animModel.position.x = _am ? _am.offsetX : 0
    animModel.position.y = ANIM_WORLD_Y + (_am ? _am.offsetY : 0) + Math.sin(elapsed * 0.75) * 0.12
    animModel.position.z = _am ? _am.offsetZ : 0
    const swayAnim = Math.sin(elapsed * 0.45 + 3.3) * 0.14
    animModel.rotation.y = (_am ? _am.rotY : 0.85) + userRot.ma + swayAnim
    animModel.rotation.z = 0

    // Visible during animation section — starts after env model is fully gone (p_anim_t > 0.45)
    const animModelOpacity = Math.min(Math.max((p_anim_t - 0.45) / 0.35, 0), 1) * (1 - animOut)
    animModel.visible = animModelOpacity > 0.01
    if (animModel.visible && !animWasVisible) { userRot.ma = 0; rotVelocity.ma = 0 }
    animWasVisible = animModel.visible
    const animDelta = animClock.getDelta()
    if (animMixer) animMixer.update(animDelta)

    // ── Cone bones — slow sine pulse so rig breathes in/out ─────────────────
    const skelPulse   = Math.sin(elapsed * 0.7) * 0.5 + 0.5   // 0→1 sine
    const skelOpacity = skelPulse * 0.70 * animModelOpacity
    if (boneViz) boneViz.update(skelOpacity)

    // ── Timeline — update playhead + frame counter ───────────────────────────
    if (mainClip && animMixer) {
      const t          = animMixer.time % mainClip.duration
      const pct        = (t / mainClip.duration) * 100
      const frame      = Math.floor(t * FPS)
      tlPlayhead.style.left    = `${pct}%`
      tlFrameDisp.textContent  = String(frame).padStart(3, '0')
    }
    tlEl.style.opacity = String(Math.min(animIn * (1 - animOut) * 1.0, 1))

    // During snap tween: disable expensive post-FX passes to keep 60fps
    const snapping = isTweening()
    if (!isMobile) {
      lensBlurPass.enabled = !snapping
      bloomPass.enabled     = !snapping
      grainPass.enabled     = !snapping
      if (!snapping) {
        lensBlurPass.uniforms['strength'].value = 0.25 + easeInOut(p2t) * (1 - easeInOut(p3t)) * 0.75
        grainPass.uniforms['time'].value = elapsed
      }
    }

    // ── Momentum decay — all models coast after drag release ──────────────────
    const anyDragActive = modelDragActive || (modelTouchLastX !== null)
    for (const { key } of modelMap) {
      if (anyDragActive && (key === modelDragKey || key === modelTouchKey)) continue
      if (Math.abs(rotVelocity[key]) > 0.0001) {
        userRot[key]      += rotVelocity[key]
        rotVelocity[key]  *= 0.90    // decay — feels like spinning on low friction
      } else {
        rotVelocity[key] = 0
      }
    }

    // ── Cursor + rotate hint ───────────────────────────────────────────────────
    const hoverKey = !modelDragActive ? getCachedHoverKey() : modelDragKey
    if (!modelDragActive) carCanvas.style.cursor = hoverKey ? 'grab' : 'default'
    rotateHint.style.opacity = '0'

    // Scroll hint — fade out once user starts scrolling
    scrollHint.style.opacity = scroll < 35 ? '1' : '0'

    // Section progress dots — highlight active section
    const activeDot = scroll >= P_CONTACT_END * 0.9 ? 7 : scroll >= P_COLLECT_END * 0.9 ? 6 : scroll >= P_ANIM_END * 0.9 ? 5 : scroll >= P_ENV_END * 0.9 ? 4 : scroll >= P5_END * 0.9 ? 3 : scroll >= P2_END * 0.9 ? 2 : scroll >= 600 ? 1 : 0
    dots.forEach((dot, i) => dot.classList.toggle('active', i === activeDot))

    // Nav link highlight — sections map to: Home, Studio, 3D Art, Animation, Contact
    // (Collectibles has no nav link → -1 highlights nothing)
    const NAV_MAP = [0, 1, 2, 2, 2, 3, -1, 4]
    navLinks.forEach((a, i) => a.classList.toggle('active', NAV_MAP[activeDot] === i))

    // Particles: during env section, place them at the lookAt target (terrain area)
    // instead of camera height, so they're visible in the isometric view.
    // Size multiplier 4x + spread multiplier 5x for env (camera is at z=20 vs z=5).
    // Spread fans particles across the full viewport so the background looks full.
    const particleY      = lerp(finalCamY, ENV_WORLD_Y + 2, envBlend)
    const inContact       = contactBlend > 0.05
    const particleSize    = lerp(lerp(1, 1.2, contactBlend), 4, envBlend)
    const particleSpread  = lerp(lerp(1, 1.3, contactBlend), 5, envBlend)
    const particleOpacity = lerp(lerp(0.55, 0.55, contactBlend), 0.55, envBlend)
    // Pick the active model's rotation to drive particle drift and sky tilt.
    // Priority: anim → env → characters → about LG → hero (default)
    let activeModelRot = model1.rotation.y
    if      (p_anim_e > 0.5)            activeModelRot = animModel.rotation.y
    else if (envBlend > 0.5)            activeModelRot = envModel.rotation.y
    else if (p2t > 0.3 && p5t < 0.8)   activeModelRot = model6.rotation.y
    else if (aboutOpacity > 0.4)        activeModelRot = lgFinalRot

    // Subtle sky tilt — nudge the nebula dome as the active model spins
    skyMesh.rotation.y = activeModelRot * 0.06

    updateParticles(elapsed, activeModelRot, particleY, particleSize, particleSpread, particleOpacity, inContact)

    // Both platforms use EffectComposer — mobile gets bloom
    // + vignette, but skips FXAA/grain/chromatic aberration for performance
    composer.render()


    } catch (err) { console.error('[animate]', err) }
  }

  animate()
}

init()
